import React, { useState, useEffect, useRef } from "react";
import { Analytics, track } from "@vercel/analytics/react";
import { PetlingSuitePage, PetlingContinuation } from "./petling/PetlingSuite";
import { getPetlingPageByPath } from "./petling/content";
import { IMAGES } from "./petling/images";

// ── CONSTANTS ──────────────────────────────────────────────────────────────
const RESUME_LINK = "https://drive.google.com/file/d/1M3GYcnnSx7CsbRa1LbqW20Xh5f0GgSkk/view?usp=sharing";

// ── RESUME TRACK HELPER ────────────────────────────────────────────────────
const handleResumeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  window.history.pushState({}, "", "/resume");
  setTimeout(() => {
    window.open(RESUME_LINK, "_blank");
    window.history.pushState({}, "", "/");
  }, 100);
};

const chipStyles: Record<string, React.CSSProperties> = {
  mint: { background: "#e0f5f0", color: "#2a9d8a", border: "1px solid rgba(125,212,192,0.3)" },
  lav:  { background: "#f0ecf8", color: "#6a5a9a", border: "1px solid rgba(184,168,212,0.3)" },
  sky:  { background: "#e8f2fa", color: "#3a6a9a", border: "1px solid rgba(168,200,232,0.3)" },
};

// ── TYPES ──────────────────────────────────────────────────────────────────
interface Project {
  id: string; title: string; subtitle: string; category: string;
  image: string; description: string; accent: string; emoji?: string;
  figmaLink?: string; year: string;
  role?: string; liveLink?: string; liveLabel?: string;
  chips?: { label: string; style: "mint" | "lav" | "sky" }[];
  isComingSoon?: boolean;
  hasCaseStudy?: boolean;
}

// ── PROJECTS DATA ──────────────────────────────────────────────────────────
const PROJECTS: Project[] = [
  {
    id: "petling-app",
    title: "Petling Mobile App",
    subtitle: "Pet Health App · Founder & Product Designer",
    category: "App",
    image: IMAGES["card-app"].url,
    description: "The live product that grew out of PawPal: a health and care companion for pet parents, designed and built end to end, and shipped on the App Store and Google Play.",
    accent: "#7dd4c0", year: "2026–Now", role: "Founder & Designer",
    liveLink: "https://petling.app/get.html", liveLabel: "Try the App ↗︎",
    chips: [{ label: "Live on iOS & Android", style: "mint" }, { label: "Solo-Built", style: "lav" }, { label: "AI-Assisted Dev", style: "sky" }],
    hasCaseStudy: true,
  },
  {
    id: "petling-admin",
    title: "Petling Admin",
    subtitle: "Admin Console · Founder & Product Designer",
    category: "Web",
    image: IMAGES["card-admin"].url,
    description: "The private control room for Petling: a dashboard, user management, feature flags per tier, monetization controls, and onboarding for daycare and boarding partners.",
    accent: "#7dd4c0", year: "2026", role: "Founder & Designer",
    chips: [{ label: "Admin Console", style: "mint" }, { label: "Feature Flags", style: "mint" }, { label: "Internal Tool", style: "mint" }],
    hasCaseStudy: true,
  },
  {
    id: "petling-providers",
    title: "Petling for Providers",
    subtitle: "B2B Platform · Founder & Product Designer",
    category: "Web",
    image: IMAGES["card-providers"].url,
    description: "A web desk for daycare and boarding teams: check pets in, log the day, answer requests, and keep pet parents updated live inside the Petling app.",
    accent: "#7dd4c0", year: "2026", role: "Founder & Designer",
    chips: [{ label: "B2B Platform", style: "mint" }, { label: "Responsive Web", style: "mint" }, { label: "In Pilot", style: "mint" }],
    hasCaseStudy: true,
  },
  {
    id: "petling-website",
    title: "Petling Website & Brand",
    subtitle: "Commercial Website · Brand Identity",
    category: "Web",
    image: IMAGES["card-website"].url,
    description: "petling.app and the brand system behind it: a story-led homepage, smart download and invite pages, an animated logo and launch assets.",
    accent: "#7dd4c0", year: "2026", role: "Brand & Web Designer",
    liveLink: "https://petling.app", liveLabel: "Visit petling.app ↗︎",
    chips: [{ label: "Web", style: "mint" }, { label: "Brand Identity", style: "mint" }, { label: "Launch Assets", style: "mint" }],
    hasCaseStudy: true,
  },
  {
    id: "pawpal",
    title: "PawPal",
    subtitle: "Original Concept · Now Petling",
    category: "App",
    image: "https://i.ibb.co/21sS6H9L/897shots-so.png",
    description: "The original Figma concept for a pet care app, covering adoption, health tracking and community. It grew into Petling, now live on iOS and Android with its own admin console and provider platform.",
    accent: "#f4a07a", emoji: "🐾", year: "2023–Now",
    figmaLink: "https://www.figma.com/design/jqlb6eWvaoYXcaoiMWqNpY/PawPal--Lobna-AbouHussein?node-id=0-1",
    chips: [{ label: "Original Concept", style: "mint" }, { label: "Figma Design", style: "sky" }, { label: "Became Petling", style: "lav" }],
    hasCaseStudy: true,
  },
  {
    id: "buildup-app",
    title: "Build-Up",
    subtitle: "Super App · X-INITY",
    category: "App",
    image: "https://i.ibb.co/PfSnr3t/Sign-up.png",
    description: "A sports super app designed from scratch for an entire ecosystem — athletes, coaches, nutritionists, physiotherapists, gyms, clubs, and federations. Seven personas. One cohesive experience.",
    accent: "#7dd4c0", emoji: "🏟️", year: "2023–Now",
    chips: [{ label: "Super App", style: "mint" }, { label: "B2B · B2C · B2G", style: "sky" }, { label: "7+ Personas", style: "lav" }],
    isComingSoon: true,
  },
  {
    id: "buildup-website",
    title: "Build-Up Website",
    subtitle: "Commercial Website · X-INITY",
    category: "Web",
    image: "https://i.ibb.co/mCxJLG4P/builddupp-website.png",
    description: "The commercial website for the Build-Up sports ecosystem — connecting athletes, clubs, and service providers across Egypt with a bilingual experience.",
    accent: "#f5c87a", emoji: "🌐", year: "2024",
    figmaLink: "https://www.figma.com/design/8MVczKfOo3fj39tyCQWiQj/Build-Up-Website--Lobna-AbouHussein?node-id=0-1",
    chips: [{ label: "Web", style: "sky" }, { label: "Bilingual", style: "mint" }],
  },
  {
    id: "shift",
    title: "Shift",
    subtitle: "HR Application · CODID",
    category: "App",
    image: "https://i.ibb.co/N2NG1b0m/shiffttt.png",
    description: "HR management platform streamlining workforce scheduling, leave management, and employee engagement across web and mobile.",
    accent: "#b8a8d4", emoji: "👥", year: "2023",
    figmaLink: "https://www.figma.com/design/OJgA3g1CpPSir99EGv35wM/Shift--Lobna-AbouHussein?node-id=0-1",
    chips: [{ label: "Web & Mobile", style: "lav" }, { label: "HR Tech", style: "sky" }],
  },
  {
    id: "fitquest",
    title: "FitQuest",
    subtitle: "Workout & Fitness App",
    category: "App",
    image: "https://i.ibb.co/xtMSmKYF/fituesstt.png",
    description: "Gamified workout experience with personalized onboarding, performance monitoring, and real-time tracking to make fitness actually fun.",
    accent: "#1e8a8a", emoji: "💪", year: "2025",
    figmaLink: "https://www.figma.com/design/hz7DWdX0a78SAw4InE8CTz/FitQuest--Lobna-AbouHussein?node-id=0-1",
    chips: [{ label: "Gamification", style: "sky" }, { label: "Mobile App", style: "mint" }],
    hasCaseStudy: true,
  },
  {
    id: "designsystem",
    title: "Design System",
    subtitle: "Petling Component Library",
    category: "System",
    image: "https://i.ibb.co/NdBTgvnd/designn-systeemm.png",
    description: "Comprehensive UI kit and component library focused on warmth and scalability — tokens, patterns, and documentation for the full Petling ecosystem.",
    accent: "#7dd4c0", emoji: "🎨", year: "2023",
    figmaLink: "https://www.figma.com/design/Xg6Ydn8KPaOzaqmyeLawU0/Design-systems-Lobna-AbouHussein?node-id=0-1",
    chips: [{ label: "Component Library", style: "mint" }, { label: "Design Tokens", style: "lav" }],
  },
];

const FILTERS = ["All", "App", "Web", "System"];

// ── CUSTOM CURSOR ─────────────────────────────────────────────────────────
function CustomCursor() {
  // No custom cursor on touch screens (it would appear wherever the finger taps)
  const isTouch = typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(hover: none), (pointer: coarse)").matches;
  const dotRef  = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const mouse   = useRef({ x: 0, y: 0 });
  const ring    = useRef({ x: 0, y: 0 });
  const raf     = useRef<number>(0);

  useEffect(() => {
    if (isTouch) return;
    const move = (e: MouseEvent) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.left = e.clientX + "px";
        dotRef.current.style.top  = e.clientY + "px";
      }
    };
    const grow = () => {
      if (dotRef.current)  { dotRef.current.style.width = "18px"; dotRef.current.style.height = "18px"; }
      if (ringRef.current) { ringRef.current.style.width = "52px"; ringRef.current.style.height = "52px"; }
    };
    const shrink = () => {
      if (dotRef.current)  { dotRef.current.style.width = "10px"; dotRef.current.style.height = "10px"; }
      if (ringRef.current) { ringRef.current.style.width = "34px"; ringRef.current.style.height = "34px"; }
    };
    document.addEventListener("mousemove", move);
    document.querySelectorAll("a,button,[data-hover]").forEach(el => {
      el.addEventListener("mouseenter", grow);
      el.addEventListener("mouseleave", shrink);
    });
    const animate = () => {
      ring.current.x += (mouse.current.x - ring.current.x) * 0.13;
      ring.current.y += (mouse.current.y - ring.current.y) * 0.13;
      if (ringRef.current) {
        ringRef.current.style.left = ring.current.x + "px";
        ringRef.current.style.top  = ring.current.y + "px";
      }
      raf.current = requestAnimationFrame(animate);
    };
    animate();
    return () => { document.removeEventListener("mousemove", move); cancelAnimationFrame(raf.current); };
  }, [isTouch]);

  if (isTouch) return null;

  return (
    <>
      <div ref={dotRef}  style={{ position:"fixed", width:10, height:10, background:"#7dd4c0", borderRadius:"50%", pointerEvents:"none", zIndex:99999, transform:"translate(-50%,-50%)", transition:"width 0.2s,height 0.2s", mixBlendMode:"multiply" }} />
      <div ref={ringRef} style={{ position:"fixed", width:34, height:34, border:"1.5px solid #7dd4c0", borderRadius:"50%", pointerEvents:"none", zIndex:99998, transform:"translate(-50%,-50%)", transition:"width 0.2s,height 0.2s", opacity:0.7 }} />
    </>
  );
}

// ── CONTACT FORM ──────────────────────────────────────────────────────────
function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState<"idle"|"sending"|"sent"|"error">("idle");

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xbdkgdkv", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, subject: form.subject, message: form.message, _replyto: form.email }),
      });
      if (res.ok) { setStatus("sent"); setForm({ name:"", email:"", subject:"", message:"" }); }
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  const inp: React.CSSProperties = { width:"100%", padding:"14px 18px", borderRadius:14, border:"1.5px solid rgba(0,0,0,0.1)", background:"white", fontSize:14, fontFamily:"'Nunito',sans-serif", fontWeight:400, color:"#0e0e0e", outline:"none", transition:"border-color 0.2s" };

  return (
    <form onSubmit={submit} style={{ display:"flex", flexDirection:"column", gap:14, marginTop:40, maxWidth:560, marginLeft:"auto", marginRight:"auto" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }} className="lo-form-grid">
        <input name="name"  value={form.name}  onChange={handle} required placeholder="Your Name"  style={inp} onFocus={e=>e.target.style.borderColor="#7dd4c0"} onBlur={e=>e.target.style.borderColor="rgba(0,0,0,0.1)"} />
        <input name="email" value={form.email} onChange={handle} required placeholder="Your Email" style={inp} type="email" onFocus={e=>e.target.style.borderColor="#7dd4c0"} onBlur={e=>e.target.style.borderColor="rgba(0,0,0,0.1)"} />
      </div>
      <input name="subject" value={form.subject} onChange={handle} required placeholder="Subject" style={inp} onFocus={e=>e.target.style.borderColor="#7dd4c0"} onBlur={e=>e.target.style.borderColor="rgba(0,0,0,0.1)"} />
      <textarea name="message" value={form.message} onChange={handle} required placeholder="Your message..." rows={5}
        style={{ ...inp, resize:"none", lineHeight:1.6 }}
        onFocus={e=>e.target.style.borderColor="#7dd4c0"} onBlur={e=>e.target.style.borderColor="rgba(0,0,0,0.1)"} />
      <button type="submit" disabled={status==="sending"} className="lo-btn-primary" style={{ alignSelf:"center", marginTop:8, opacity:status==="sending"?0.7:1 }}>
        {status==="sending" ? "Sending…" : status==="sent" ? "✓ Message Sent!" : "Send Message →"}
      </button>
      {status==="error" && <p style={{ textAlign:"center", color:"#e05", fontSize:13 }}>Something went wrong. Try emailing directly.</p>}
    </form>
  );
}

// ── PROJECTS SECTION ──────────────────────────────────────────────────────
const PANEL_H = 460;

function ProjectsSection({ onOpenCaseStudy }: { onOpenCaseStudy: (id: string) => void }) {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState<Project>(PROJECTS[0]);
  const [animKey, setAnimKey] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);

  const filtered = filter === "All" ? PROJECTS : PROJECTS.filter(p => p.category === filter);

  const selectProject = (p: Project) => {
    setImgLoaded(false);
    setAnimKey(k => k + 1);
    setActive(p);
    track("project_click", { title: p.title });
  };

  useEffect(() => {
    if (!filtered.find(p => p.id === active.id)) {
      setActive(filtered[0]);
      setAnimKey(k => k + 1);
    }
  }, [filter]);

  const globalIdx = PROJECTS.findIndex(p => p.id === active.id);

  const TOP_H    = 56;
  const BOTTOM_H = 52;
  const BODY_H   = PANEL_H - TOP_H - BOTTOM_H;

  return (
    <section id="projects" style={{ background:"#0d0d0d", padding:"120px 0 100px", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")", pointerEvents:"none", opacity:.4, zIndex:0 }} />
      <div style={{ position:"absolute", top:"30%", right:"-10%", width:500, height:500, background:active.accent, borderRadius:"50%", filter:"blur(130px)", opacity:.07, transition:"background 0.6s", pointerEvents:"none", zIndex:0 }} />

      <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 60px", position:"relative", zIndex:1 }}>

        <div className="lo-reveal" style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:40, flexWrap:"wrap", gap:20 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"rgba(255,255,255,.28)", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ display:"inline-block", width:24, height:1.5, background:"currentColor" }} /> Selected Work
            </div>
            <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(34px,4.5vw,58px)", letterSpacing:-2, lineHeight:.95, color:"white" }}>
              My<br /><em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#7dd4c0" }}>Projects.</em>
            </h2>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)} data-hover
                style={{ padding:"9px 20px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:12, letterSpacing:.5, textTransform:"uppercase", border:"1.5px solid", cursor:"none", transition:"all .25s",
                  background:  filter===f ? "#7dd4c0" : "transparent",
                  color:       filter===f ? "#0d0d0d" : "rgba(255,255,255,.45)",
                  borderColor: filter===f ? "#7dd4c0" : "rgba(255,255,255,.14)",
                }}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="lo-proj-split"
          style={{ display:"grid", gridTemplateColumns:"280px 1fr", gap:16, height:PANEL_H }}>

          <div style={{ height:PANEL_H, overflowY:"auto", borderRadius:18,
                        background:"rgba(255,255,255,.025)",
                        border:"1px solid rgba(255,255,255,.07)" }}
               className="lo-proj-list">
            {filtered.map((p, i) => {
              const isAct = active.id === p.id;
              return (
                <div key={p.id} onClick={() => selectProject(p)} data-hover
                  style={{ padding:"14px 16px", cursor:"none",
                           borderBottom:"1px solid rgba(255,255,255,.05)",
                           transition:"background .18s", position:"relative",
                           background: isAct ? "rgba(125,212,192,.09)" : "transparent" }}
                  onMouseEnter={e => { if (!isAct) (e.currentTarget as HTMLDivElement).style.background = "rgba(255,255,255,.04)"; }}
                  onMouseLeave={e => { if (!isAct) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                >
                  {isAct && <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:"#7dd4c0", borderRadius:"0 2px 2px 0" }} />}
                  <div style={{ display:"flex", alignItems:"center", gap:11 }}>
                    <div style={{ width:42, height:42, flexShrink:0, borderRadius:10, overflow:"hidden",
                                  border:`1px solid ${isAct ? p.accent+"55" : "rgba(255,255,255,.08)"}`,
                                  transition:"border-color .2s" }}>
                      {p.image ? (
                        <img src={p.image} alt={p.title}
                             style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
                      ) : (
                        <div style={{ width:"100%", height:"100%", background:`linear-gradient(135deg,${p.accent}66,${p.accent}18)`,
                                      display:"flex", alignItems:"center", justifyContent:"center",
                                      fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:15, color:p.accent }}>
                          {p.title.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:6 }}>
                        <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:13,
                                       color: isAct ? "white" : "rgba(255,255,255,.6)",
                                       whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                                       transition:"color .2s" }}>
                          {p.title}
                        </span>
                        {p.isComingSoon
                          ? <span style={{ fontSize:8, fontWeight:800, letterSpacing:.8, textTransform:"uppercase",
                                           background:"rgba(125,212,192,.15)", color:"#7dd4c0",
                                           padding:"2px 7px", borderRadius:100, flexShrink:0 }}>Soon</span>
                          : <span style={{ fontSize:9, color:"rgba(255,255,255,.2)", flexShrink:0 }}>→</span>}
                      </div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,.28)", marginTop:2,
                                    fontWeight:400, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {p.subtitle}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div key={animKey}
            style={{ height:PANEL_H, borderRadius:20, overflow:"hidden",
                     border:"1px solid rgba(255,255,255,.08)", background:"#161616",
                     display:"flex", flexDirection:"column",
                     animation:"fadeUp .38s cubic-bezier(.16,1,.3,1) both" }}
            className="lo-proj-card">

            <div style={{ height:TOP_H, flexShrink:0,
                          display:"flex", alignItems:"center", justifyContent:"space-between",
                          padding:"0 24px", borderBottom:"1px solid rgba(255,255,255,.06)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:40,
                               color:"rgba(255,255,255,.05)", lineHeight:1, letterSpacing:-3, userSelect:"none" }}>
                  {globalIdx + 1 < 10 ? "0" : ""}{globalIdx + 1}
                </span>
                <div>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.8, textTransform:"uppercase",
                                 color:"rgba(255,255,255,.35)" }}>{active.category}</div>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,.22)", fontWeight:400, marginTop:1 }}>{active.year}</div>
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                {active.isComingSoon && (
                  <span style={{ fontSize:9, fontWeight:800, letterSpacing:.8, textTransform:"uppercase",
                                 background:"rgba(125,212,192,.12)", color:"#7dd4c0",
                                 border:"1px solid rgba(125,212,192,.25)", padding:"4px 10px", borderRadius:100 }}>
                    Coming Soon
                  </span>
                )}
                {active.emoji && <span style={{ fontSize:20 }}>{active.emoji}</span>}
              </div>
            </div>

            <div className="lo-card-body"
              style={{ flex:1, minHeight:0,
                       display:"grid", gridTemplateColumns:"1fr 1fr" }}>

              <div style={{ position:"relative", overflow:"hidden",
                            background:"#0e0e0e",
                            borderRight:"1px solid rgba(255,255,255,.06)" }}>
                {[{t:10,l:10},{t:10,r:10},{b:10,l:10},{b:10,r:10}].map((pos,di) => (
                  <div key={di} style={{ position:"absolute", width:4, height:4, borderRadius:"50%",
                                         background:active.accent, opacity:.4, transition:"background .5s", zIndex:2,
                                         top:pos.t, bottom:(pos as any).b, left:pos.l, right:(pos as any).r }} />
                ))}
                <div style={{ position:"absolute", inset:12, borderRadius:10, overflow:"hidden",
                              border:`1px solid ${active.accent}30`,
                              boxShadow:`inset 0 0 0 1px rgba(0,0,0,.4)` }}>
                  {active.image ? (
                    <img src={active.image} alt={active.title} onLoad={() => setImgLoaded(true)}
                      style={{ width:"100%", height:"100%",
                               objectFit:"cover", display:"block",
                               transition:"opacity .4s, transform .5s",
                               opacity: imgLoaded ? 1 : 0,
                               transform: imgLoaded ? "none" : "scale(1.04)" }} />
                  ) : (
                    <div style={{ width:"100%", height:"100%", background:`linear-gradient(135deg,${active.accent}40,${active.accent}0d)`,
                                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8 }}>
                      <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:26, color:active.accent, letterSpacing:-1 }}>{active.title}</span>
                      <span style={{ fontSize:10, fontWeight:700, letterSpacing:1.6, textTransform:"uppercase", color:"rgba(255,255,255,.3)" }}>Preview coming soon</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="lo-card-text" style={{ overflow:"hidden", padding:"22px 24px",
                            display:"flex", flexDirection:"column", gap:16 }}>
                <div style={{ flex:1, minHeight:0, overflow:"hidden" }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.4, textTransform:"uppercase",
                                color:"rgba(255,255,255,.3)", marginBottom:8 }}>
                    {active.subtitle}
                  </div>
                  <h3 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900,
                               fontSize:"clamp(20px,2.2vw,28px)", letterSpacing:-0.7,
                               color:"white", lineHeight:1.05, marginBottom:12 }}>
                    {active.title}
                  </h3>
                  <p style={{ fontSize:12, color:"rgba(255,255,255,.44)", lineHeight:1.72, fontWeight:300,
                              marginBottom:14,
                              display:"-webkit-box", WebkitLineClamp:4,
                              WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                    {active.description}
                  </p>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {active.chips?.map((c,i) => (
                      <span key={i} style={{ fontSize:9, fontWeight:700, padding:"3px 10px",
                                             borderRadius:100, letterSpacing:.3, ...chipStyles[c.style] }}>
                        {c.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ flexShrink:0 }}>
                  <div style={{ height:2, borderRadius:100, background:"rgba(255,255,255,.06)", marginBottom:14, overflow:"hidden" }}>
                    <div style={{ height:"100%", borderRadius:100,
                                  background:`linear-gradient(90deg,${active.accent},${active.accent}88)`,
                                  width:`${((globalIdx+1)/PROJECTS.length)*100}%`,
                                  transition:"width .5s cubic-bezier(.16,1,.3,1), background .5s" }} />
                  </div>
                  <div className="lo-card-foot" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,.2)", fontWeight:400 }}>
                      {globalIdx + 1} / {PROJECTS.length}
                    </span>
                    <div className="lo-card-btns" style={{ display:"flex", gap:8, alignItems:"center", minHeight:38 }}>
                      {active.isComingSoon ? (
                        <span style={{ fontSize:11, color:"rgba(255,255,255,.2)", fontStyle:"italic", fontWeight:300 }}>
                          Case study coming soon ✦
                        </span>
                      ) : (
                        <>
                          {active.hasCaseStudy && (
                            <button
                              onClick={() => onOpenCaseStudy(active.id)}
                              data-hover
                              style={{ display:"inline-flex", alignItems:"center", gap:7,
                                       background:"transparent",
                                       color:"rgba(255,255,255,.7)",
                                       padding:"9px 16px", borderRadius:100,
                                       fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:11,
                                       border:`1.5px solid rgba(255,255,255,.18)`,
                                       cursor:"none", letterSpacing:.4,
                                       transition:"all .2s" }}
                              onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor="rgba(255,255,255,.4)"; b.style.color="white"; }}
                              onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor="rgba(255,255,255,.18)"; b.style.color="rgba(255,255,255,.7)"; }}>
                              Case Study ↗︎
                            </button>
                          )}
                          {(active.figmaLink || active.liveLink) && (
                          <button onClick={() => { const url = active.figmaLink || active.liveLink; if (url) window.open(url,"_blank"); }} data-hover
                            style={{ display:"inline-flex", alignItems:"center", gap:7,
                                     background:active.accent, color:"#0d0d0d",
                                     padding:"9px 18px", borderRadius:100,
                                     fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:11,
                                     border:"none", cursor:"none", letterSpacing:.4,
                                     transition:"transform .2s, box-shadow .2s, background .4s",
                                     boxShadow:`0 6px 22px ${active.accent}44` }}
                            onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.transform="translateY(-2px)"; b.style.boxShadow=`0 12px 32px ${active.accent}66`; }}
                            onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.transform="none"; b.style.boxShadow=`0 6px 22px ${active.accent}44`; }}>
                            {active.figmaLink ? "Open in Figma ↗︎" : (active.liveLabel || "Open ↗︎")}
                          </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ height:BOTTOM_H, flexShrink:0,
                          borderTop:"1px solid rgba(255,255,255,.06)",
                          padding:"0 24px", display:"flex", alignItems:"center",
                          justifyContent:"space-between", gap:12 }}>
              <div style={{ display:"flex", gap:20 }}>
                {[{label:"Type",val:active.category},{label:"Year",val:active.year},{label:"Role",val:active.role || "Lead UI/UX"}].map((f,i) => (
                  <div key={i}>
                    <div style={{ fontSize:8, fontWeight:700, letterSpacing:1.2, textTransform:"uppercase",
                                  color:"rgba(255,255,255,.2)", marginBottom:2 }}>{f.label}</div>
                    <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontSize:11,
                                  fontWeight:700, color:"rgba(255,255,255,.55)" }}>{f.val}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {(["prev","next"] as const).map(dir => {
                  const ti = dir === "prev" ? globalIdx - 1 : globalIdx + 1;
                  const disabled = ti < 0 || ti >= PROJECTS.length;
                  return (
                    <button key={dir} data-hover onClick={() => !disabled && selectProject(PROJECTS[ti])}
                      style={{ width:30, height:30, borderRadius:"50%",
                               border:"1px solid rgba(255,255,255,.12)", background:"transparent",
                               color: disabled ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.55)",
                               fontSize:13, display:"flex", alignItems:"center", justifyContent:"center",
                               cursor: disabled ? "default" : "none", transition:"all .2s" }}
                      onMouseEnter={e => { if (!disabled) { const b=e.currentTarget as HTMLButtonElement; b.style.background="rgba(255,255,255,.07)"; b.style.borderColor="rgba(255,255,255,.28)"; }}}
                      onMouseLeave={e => { const b=e.currentTarget as HTMLButtonElement; b.style.background="transparent"; b.style.borderColor="rgba(255,255,255,.12)"; }}>
                      {dir === "prev" ? "←" : "→"}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height:60, background:"linear-gradient(to bottom, #0d0d0d, #0e0e0e)" }} />
    </section>
  );
}

// ── PAWPAL CASE STUDY PAGE ────────────────────────────────────────────────
function PetlingCaseStudy() {
  const onBack = () => {
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const FIGMA_LINK = "https://www.figma.com/design/jqlb6eWvaoYXcaoiMWqNpY/PawPal--Lobna-AbouHussein?node-id=0-1";
  const APP_LINK = "https://petling.app/get.html";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const sectionLabel = (num: string, text: string) => (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
      <div style={{ width:32, height:1.5, background:"#3a7d6a" }} />
      <span style={{ fontSize:10, fontWeight:700, letterSpacing:2.5, textTransform:"uppercase", color:"#3a7d6a" }}>
        {num} — {text}
      </span>
    </div>
  );

  const PrincipleCard = ({ title, body, dark }: { title: string; body: string; dark?: boolean }) => (
    <div style={{ background: dark ? "#1a3d30" : "#f0f9f5", border:`1px solid ${dark ? "rgba(125,212,192,.2)" : "rgba(58,125,106,.12)"}`, borderRadius:16, padding:"24px 28px" }}>
      {dark && <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"rgba(125,212,192,.6)", marginBottom:10 }}>Design Principle</div>}
      <p style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontSize:17, lineHeight:1.65, color: dark ? "rgba(255,255,255,.85)" : "#1a3d30" }}>
        "{body}"
      </p>
    </div>
  );

  const ProblemCard = ({ num, title, body }: { num: string; title: string; body: string }) => (
    <div style={{ background:"#161616", border:"1px solid rgba(255,255,255,.08)", borderRadius:18, padding:"32px 28px", borderTop:`3px solid #3a7d6a`, flex:1 }}>
      <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontSize:44, fontWeight:900, color:"rgba(255,255,255,.05)", lineHeight:1, marginBottom:20, letterSpacing:-2 }}>{num}</div>
      <h3 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:16, color:"white", marginBottom:10, lineHeight:1.3 }}>{title}</h3>
      <p style={{ fontSize:13, color:"rgba(255,255,255,.45)", lineHeight:1.75, fontWeight:300 }}>{body}</p>
    </div>
  );

  const PersonaCard = ({ emoji, role, name, quote, desc }: { emoji: string; role: string; name: string; quote: string; desc: string }) => (
    <div style={{ background:"white", border:"1px solid rgba(0,0,0,.08)", borderRadius:18, padding:"28px 24px", flex:1, boxShadow:"0 4px 24px rgba(0,0,0,.06)" }}>
      <div style={{ width:44, height:44, borderRadius:12, background:"#e0f5f0", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, marginBottom:16 }}>{emoji}</div>
      <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"#3a7d6a", marginBottom:6 }}>{role}</div>
      <h3 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:17, color:"#0e0e0e", marginBottom:10 }}>{name}</h3>
      <p style={{ fontSize:13, color:"#555", lineHeight:1.72, fontWeight:300, marginBottom:16 }}>{desc}</p>
      <div style={{ background:"#f0f9f5", border:"1px solid rgba(58,125,106,.15)", borderLeft:"3px solid #3a7d6a", borderRadius:"0 10px 10px 0", padding:"12px 16px" }}>
        <p style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontSize:13, color:"#1a3d30", lineHeight:1.65 }}>"{quote}"</p>
      </div>
    </div>
  );

  const ProcessStep = ({ num, title, desc }: { num: string; title: string; desc: string }) => (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", flex:1 }}>
      <div style={{ width:48, height:48, borderRadius:"50%", background:"#3a7d6a", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:14, color:"white", marginBottom:16, flexShrink:0, position:"relative", zIndex:1 }}>{num}</div>
      <h4 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:14, color:"#0e0e0e", marginBottom:8 }}>{title}</h4>
      <p style={{ fontSize:12, color:"#666", lineHeight:1.7, fontWeight:300 }}>{desc}</p>
    </div>
  );

  const TakeawayCard = ({ num, title, body }: { num: string; title: string; body: string }) => (
    <div style={{ background:"white", border:"1px solid rgba(0,0,0,.07)", borderLeft:`3px solid #3a7d6a`, borderRadius:"0 16px 16px 0", padding:"28px 24px", boxShadow:"0 2px 16px rgba(0,0,0,.05)" }}>
      <div style={{ fontSize:10, fontWeight:700, color:"rgba(58,125,106,.5)", letterSpacing:1, marginBottom:8 }}>{num}</div>
      <h3 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:15, color:"#0e0e0e", marginBottom:8, lineHeight:1.3 }}>{title}</h3>
      <p style={{ fontSize:13, color:"#555", lineHeight:1.72, fontWeight:300 }}>{body}</p>
    </div>
  );

  return (
    <div style={{ background:"#f8f7f4", minHeight:"100vh" }}>

      {/* ── STICKY TOP BAR ── */}
      <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:1000, background:"rgba(248,247,244,.97)", backdropFilter:"blur(14px)", borderBottom:"1px solid rgba(0,0,0,.06)", height:64, display:"flex", alignItems:"center", padding:"0 60px", justifyContent:"space-between" }} className="cs-topbar-pad">
        <button onClick={onBack} data-hover
          style={{ display:"flex", alignItems:"center", gap:8, background:"none", border:"none", cursor:"none", fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:13, color:"#555", padding:0, transition:"color .2s" }}
          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "#0e0e0e"}
          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "#555"}>
          ← Back to Portfolio
        </button>
        <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:16, letterSpacing:-.5 }}>
          Lobna<span style={{ color:"#7dd4c0" }}>.</span>
        </span>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <a href={FIGMA_LINK} target="_blank" rel="noreferrer"
            style={{ color:"#555", padding:"9px 14px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:12, display:"flex", alignItems:"center", gap:6, transition:"all .25s" }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = "#0e0e0e"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = "#555"; }}>
            View in Figma ↗︎
          </a>
          <a href={APP_LINK} target="_blank" rel="noreferrer"
            style={{ background:"#0e0e0e", color:"white", padding:"9px 22px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:12, display:"flex", alignItems:"center", gap:6, transition:"all .25s" }}
            onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background="#3a7d6a"; }}
            onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background="#0e0e0e"; }}>
            Try Petling ↗︎
          </a>
        </div>
      </div>

      {/* ── HERO ── */}
      <div style={{ paddingTop:64, background:"#f4f0e8", borderBottom:"1px solid rgba(0,0,0,.06)", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-80, right:-80, width:400, height:400, background:"#f4a07a", borderRadius:"50%", filter:"blur(100px)", opacity:.15, pointerEvents:"none" }} />
        <div style={{ position:"absolute", bottom:-60, left:-60, width:300, height:300, background:"#3a7d6a", borderRadius:"50%", filter:"blur(80px)", opacity:.1, pointerEvents:"none" }} />

        <div style={{ maxWidth:1200, margin:"0 auto", padding:"80px 60px 0" }} className="cs-section-pad">
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:28 }}>
            <div style={{ background:"#e0f5f0", border:"1px solid rgba(58,125,106,.3)", borderRadius:100, padding:"5px 14px", display:"inline-flex", alignItems:"center", gap:6 }}>
              <div style={{ width:6, height:6, background:"#3a7d6a", borderRadius:"50%" }} />
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"#3a7d6a" }}>Case Study · Live Product · 2023–2026</span>
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"flex-end" }} className="cs-hero-grid">
            <div>
              <h1 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(56px,8vw,100px)", letterSpacing:-4, lineHeight:.88, color:"#0e0e0e", marginBottom:0 }}>
                Petling
              </h1>
              <h1 style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, fontSize:"clamp(44px,6vw,80px)", letterSpacing:-2, lineHeight:.95, color:"#3a7d6a", marginBottom:24 }}>
                Care.
              </h1>
              <p style={{ fontSize:17, color:"#555", lineHeight:1.75, fontWeight:300, maxWidth:480, marginBottom:36 }}>
                A comprehensive mobile platform unifying pet adoption, health tracking, and social community — designed, independently built, and shipped by me, live on the App Store and Google Play.
              </p>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:48 }}>
                {["UI/UX Design","UX Research","AI-Assisted Development","Design System","Shipped Product"].map((tag, i) => (
                  <span key={i} style={{ background: i===0 ? "#3a7d6a" : "transparent", color: i===0 ? "white" : "#3a7d6a", border:"1.5px solid", borderColor: i===0 ? "#3a7d6a" : "rgba(58,125,106,.35)", borderRadius:100, padding:"6px 16px", fontSize:11, fontWeight:700, fontFamily:"'Cabinet Grotesk',sans-serif", letterSpacing:.3 }}>
                    {tag}
                  </span>
                ))}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:24, paddingTop:36, borderTop:"1px solid rgba(0,0,0,.08)" }}>
                {[{label:"Role",val:"Founder & Product Designer"},{label:"Timeline",val:"2023 – Present"},{label:"Platform",val:"iOS & Android · Live"},{label:"Deliverables",val:"Research, UI, Full Build, Launch"}].map((m,i) => (
                  <div key={i}>
                    <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"#999", marginBottom:4 }}>{m.label}</div>
                    <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:13, color:"#0e0e0e" }}>{m.val}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"flex-end", paddingBottom:0 }}>
              <img
                src="https://i.ibb.co/21sS6H9L/897shots-so.png"
                alt="Petling App"
                style={{ width:"100%", maxWidth:520, borderRadius:"24px 24px 0 0", objectFit:"cover", display:"block" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── SHIPPED PRODUCT BAND ── */}
      <div style={{ background:"#1a3d30", padding:"48px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", flexWrap:"wrap", gap:32, alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ maxWidth:520 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"#7dd4c0", marginBottom:8 }}>
              Beyond the Figma file
            </div>
            <h3 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:20, color:"white", lineHeight:1.35, marginBottom:0 }}>
              This design didn't stay a prototype. I took it the rest of the way myself — research through a live app real people use.
            </h3>
          </div>
          <div style={{ display:"flex", gap:28, flexWrap:"wrap" }}>
            {[
              { label:"Solo-built", val:"Design + Dev" },
              { label:"Live on", val:"iOS & Android" },
              { label:"Now expanding to", val:"B2B Provider Platform" },
            ].map((s,i) => (
              <div key={i}>
                <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:17, color:"#7dd4c0", marginBottom:2 }}>{s.val}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,.5)" }}>{s.label}</div>
              </div>
            ))}
          </div>
          <a href={APP_LINK} target="_blank" rel="noreferrer"
            style={{ background:"#7dd4c0", color:"#0e0e0e", padding:"12px 26px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:13, display:"inline-flex", alignItems:"center", gap:8, textDecoration:"none", whiteSpace:"nowrap" }}>
            Try Petling ↗︎
          </a>
        </div>
      </div>

      {/* ── 01 OVERVIEW ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("01", "Overview")}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:80, alignItems:"center" }} className="cs-two-col">
            <div>
              <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:24 }}>
                Everything your pet needs,{" "}
                <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#3a7d6a" }}>one place.</em>
              </h2>
              <p style={{ fontSize:15, color:"#555", lineHeight:1.85, fontWeight:300, marginBottom:20 }}>
                Pet ownership is growing fast — but the tools available are fragmented. Adoption happens on unverified Facebook groups. Health records live in WhatsApp messages. Community is scattered across Instagram accounts that weren't built for this purpose.
              </p>
              <p style={{ fontSize:15, color:"#555", lineHeight:1.85, fontWeight:300, marginBottom:28 }}>
                Petling unifies all of this into one intuitive, emotionally intelligent platform — serving adopters, current owners, and the broader community without any user feeling overwhelmed by features meant for someone else.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {[
                  { icon:"🐾", title:"Adopt Responsibly", desc:"Browse verified shelters, filter by breed, age, and location, complete the entire adoption application in-app." },
                  { icon:"❤️", title:"Track Their Health", desc:"Log vaccinations, medications, weight trends, and vet visits. Calm awareness, not medical anxiety." },
                  { icon:"✨", title:"Connect & Share", desc:"Post moments, follow other owners, discover local playdates and events — built for the pet community." },
                ].map((item, i) => (
                  <div key={i} style={{ display:"flex", gap:12, padding:"14px 16px", background:"white", borderRadius:12, border:"1px solid rgba(0,0,0,.07)", boxShadow:"0 2px 10px rgba(0,0,0,.04)" }}>
                    <div style={{ width:36, height:36, borderRadius:9, background:"#e0f5f0", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>{item.icon}</div>
                    <div>
                      <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:13, color:"#0e0e0e", marginBottom:3 }}>{item.title}</div>
                      <div style={{ fontSize:11, color:"#666", lineHeight:1.6, fontWeight:300 }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 24px 64px rgba(0,0,0,.12)" }}>
              <img
                src="https://i.ibb.co/9H69mMnS/218a8a236560597-68ee6e5ebe1ee.png"
                alt="Petling adoption and health screens"
                style={{ width:"100%", display:"block" }}
              />
            </div>
          </div>
          <div style={{ marginTop:48 }}>
            <PrincipleCard title="" body="The hardest UX problem is never the feature itself — it's the emotional journey that surrounds it. Adoption isn't just a form. Health tracking isn't just a chart. They're acts of care." />
          </div>
        </div>
      </div>

      {/* ── 02 THE PROBLEM ── */}
      <div style={{ background:"#0e0e0e", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("02", "The Problem")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(32px,4.5vw,56px)", letterSpacing:-2, lineHeight:.95, color:"white", marginBottom:12 }}>
            Three broken{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#7dd4c0" }}>experiences.</em>
          </h2>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.45)", lineHeight:1.8, maxWidth:560, marginBottom:48, fontWeight:300 }}>
            Through user interviews and secondary research, three pain points emerged consistently — each representing a gap where empathy-driven design could make a real difference.
          </p>
          <div style={{ display:"flex", gap:16 }} className="cs-three-col">
            <ProblemCard num="01" title="Adoption is emotionally taxing and logistically broken" body="Prospective adopters scroll through unverified Facebook posts with no standardised way to apply. Shelters receive inconsistent enquiries and have no tools to vet applicants or manage follow-ups." />
            <ProblemCard num="02" title="Health records are scattered and dangerously easy to lose" body="Most pet owners rely on paper documents, WhatsApp forwards from vets, or simply their own memory to track vaccinations and medications. This leads to missed appointments and preventable health issues." />
            <ProblemCard num="03" title="The pet community has no dedicated, safe space" body="Pet owners resort to Instagram and generic Facebook groups for community. But these platforms weren't built for this — no pet profiles, no local event discovery, no way to meaningfully connect." />
          </div>
        </div>
      </div>

      {/* ── 03 PERSONAS ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("03", "Users & Personas")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:12 }}>
            Three users.{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#3a7d6a" }}>One app.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:560, marginBottom:48, fontWeight:300 }}>
            Research identified three distinct user archetypes with different primary goals and failure modes.
          </p>
          <div style={{ display:"flex", gap:20 }} className="cs-three-col">
            <PersonaCard emoji="🏠" role="The Adopter" name="Sarah, 28 · Cairo" desc="Wants to adopt responsibly but finds the current process confusing, slow, and emotionally inconsistent." quote="I just want to know the pet is healthy and the shelter is legitimate. Why is that so hard to find out?" />
            <PersonaCard emoji="📋" role="The Pet Owner" name="Ahmed, 34 · Maadi" desc="Has two dogs and struggles to keep health records organised. Misses vet appointments because reminders are scattered across apps, paper notes, and memory." quote="I forgot Kelly's vaccination last month. I need everything in one place so I can actually stay on top of it." />
            <PersonaCard emoji="🤝" role="The Community Member" name="Nour, 24 · Heliopolis" desc="Active on Instagram but wants a space built for pet owners specifically. Interested in local playdates, sharing milestones, and connecting with people who genuinely love animals." quote="Instagram doesn't get it. I want to connect with people who love their animals the way I love mine." />
          </div>
        </div>
      </div>

      {/* ── 04 UX PROCESS ── */}
      <div style={{ background:"white", padding:"80px 60px", borderTop:"1px solid rgba(0,0,0,.06)", borderBottom:"1px solid rgba(0,0,0,.06)" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("04", "UX Process")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:10 }}>
            How we got{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#3a7d6a" }}>here.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:540, marginBottom:60, fontWeight:300 }}>
            A full double-diamond process compressed into a focused 2-month sprint.
          </p>
          <div style={{ position:"relative" }}>
            <div style={{ position:"absolute", top:24, left:"10%", right:"10%", height:2, background:"linear-gradient(90deg,#3a7d6a,#7dd4c0)", borderRadius:2, zIndex:0 }} className="cs-hide-mobile" />
            <div style={{ display:"flex", gap:0 }} className="cs-process-row">
              <ProcessStep num="01" title="Research & Empathy" desc="8 user interviews across adopters, owners, and shelter staff. Identified 3 pain points and 3 user archetypes." />
              <ProcessStep num="02" title="Information Architecture" desc="Mapped all modules into tab-based nav. Card sorting with users reduced 6 initial tabs down to 4 focused sections." />
              <ProcessStep num="03" title="Wireframing" desc="Lo-fi wireframes for all critical flows. Mid-fi prototype tested with 5 users revealed the dual-profile insight." />
              <ProcessStep num="04" title="Visual Design" desc="Warm illustrated language, earthy greens and yellows. Custom illustrations for onboarding and empty states." />
              <ProcessStep num="05" title="Usability Testing" desc="Two rounds on adoption flow. Form reduced 12 → 7 fields. Document upload made optional after users hesitated." />
            </div>
          </div>
        </div>
      </div>

      {/* ── 05 ONBOARDING ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("05", "Onboarding")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:16 }}>
            Earn attention{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#3a7d6a" }}>before asking for it.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:580, marginBottom:48, fontWeight:300 }}>
            Four illustrated screens introduce Petling's three core pillars before any personal information is requested.
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 16px 48px rgba(0,0,0,.10)" }}>
              <img src="https://i.ibb.co/3m0ZpwQN/16dfaa236560597-68ee6e5ebec55.png" alt="Petling onboarding screens 1-2" style={{ width:"100%", display:"block" }} />
            </div>
            <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 16px 48px rgba(0,0,0,.10)" }}>
              <img src="https://i.ibb.co/s9CszbW3/fd6eaf236560597-68ee6e5ec099d.png" alt="Petling onboarding screens 3-4" style={{ width:"100%", display:"block" }} />
            </div>
          </div>
        </div>
      </div>

      {/* ── 06 ADOPTION FLOW ── */}
      <div style={{ background:"#0e0e0e", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("06", "Adoption Flow")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"white", marginBottom:16 }}>
            Trust by{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#7dd4c0" }}>design.</em>
          </h2>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.5)", lineHeight:1.8, maxWidth:580, marginBottom:48, fontWeight:300 }}>
            A progressive disclosure model — browse and filter first, get emotionally invested in a pet's profile, then commit to the application. The form was reduced from 12 fields to 7 after usability testing.
          </p>
          <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 16px 48px rgba(0,0,0,.3)", marginBottom:48 }}>
            <img src="https://i.ibb.co/9H69mMnS/218a8a236560597-68ee6e5ebe1ee.png" alt="Petling adoption flow screens" style={{ width:"100%", display:"block" }} />
          </div>
        </div>
      </div>

      {/* ── 07 HEALTH TRACKER ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("07", "Health Tracker")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:16 }}>
            Data without{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#3a7d6a" }}>anxiety.</em>
          </h2>
          <p style={{ fontSize:14, color:"#555", lineHeight:1.8, maxWidth:580, marginBottom:48, fontWeight:300 }}>
            The Health tab is scannable first, detailed on demand. Soft lines, muted palette, no red alerts — calm awareness, not medical anxiety.
          </p>
          <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 16px 48px rgba(0,0,0,.10)", marginBottom:48 }}>
            <img src="https://i.ibb.co/fV9nRgdZ/c582e0236560597-68ee6e5ebd65a.png" alt="Petling health dashboard" style={{ width:"100%", display:"block" }} />
          </div>
        </div>
      </div>

      {/* ── 08 TAKEAWAYS ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("08", "Takeaways")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:48 }}>
            What{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#3a7d6a" }}>Petling</em>
            {" "}taught me.
          </h2>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }} className="cs-two-col">
            <TakeawayCard num="01" title="Serve multiple users without fragmenting the experience" body="Tab-based navigation kept each persona's primary flow clean while sharing one design system. The architecture makes each user feel like the app was built specifically for them." />
            <TakeawayCard num="02" title="Testing reveals what logic alone cannot predict" body="The dual-profile insight came from watching real users interact with wireframes. Design assumptions are hypotheses until someone actually uses them." />
            <TakeawayCard num="03" title="Data visualisation and emotional design are not opposites" body="A single design token system can serve both a medical weight chart and a warm community post. The system makes them feel like siblings." />
            <TakeawayCard num="04" title="Emotional design is functional design" body="The onboarding illustrations, the adoption confirmation copy, the soft palette in the health tab — these are UX decisions that directly affect whether a user completes a flow or abandons it." />
            <TakeawayCard num="05" title="Design ownership doesn't end at handoff" body="Taking Petling from Figma to a live App Store and Google Play listing myself, using AI-assisted development, taught me to design with real technical and data constraints in mind from day one — not just visual ones." />
            <TakeawayCard num="06" title="A shipped product opens doors a prototype can't" body="Real users surfaced needs no usability test predicted — which is what led to building a second product line: a B2B platform for daycare and boarding providers." />
          </div>
        </div>
      </div>

      {/* ── THE STORY CONTINUES ── */}
      <PetlingContinuation />

      {/* ── CTA ── */}
      <div style={{ background:"#0e0e0e", padding:"80px 60px", textAlign:"center" }} className="cs-section-pad">
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:44, color:"#f8f7f4", letterSpacing:-2, lineHeight:.95, marginBottom:16 }}>
            See it live,<br />not just in<em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#7dd4c0" }}> Figma.</em>
          </div>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.45)", lineHeight:1.8, marginBottom:32, fontWeight:300 }}>
            Download the real app on iOS or Android, or explore the full design system and every screen in Figma.
          </p>
          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <a href={APP_LINK} target="_blank" rel="noreferrer"
              style={{ background:"#7dd4c0", color:"#0e0e0e", padding:"14px 32px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:14, display:"inline-flex", alignItems:"center", gap:8, transition:"all .25s", textDecoration:"none" }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-3px)"; (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 12px 36px rgba(125,212,192,.4)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.transform = "none"; (e.currentTarget as HTMLAnchorElement).style.boxShadow = "none"; }}>
              Try Petling ↗︎
            </a>
            <a href={FIGMA_LINK} target="_blank" rel="noreferrer"
              style={{ background:"transparent", color:"rgba(255,255,255,.7)", padding:"14px 32px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:14, border:"1.5px solid rgba(255,255,255,.18)", transition:"all .25s", textDecoration:"none", display:"inline-flex", alignItems:"center" }}
              onMouseEnter={e => { const b = e.currentTarget as HTMLAnchorElement; b.style.borderColor="rgba(255,255,255,.4)"; b.style.color="white"; }}
              onMouseLeave={e => { const b = e.currentTarget as HTMLAnchorElement; b.style.borderColor="rgba(255,255,255,.18)"; b.style.color="rgba(255,255,255,.7)"; }}>
              View in Figma ↗︎
            </a>
            <button onClick={onBack} data-hover
              style={{ background:"transparent", color:"rgba(255,255,255,.55)", padding:"14px 32px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:14, border:"1.5px solid rgba(255,255,255,.18)", cursor:"none", transition:"all .25s" }}
              onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor="rgba(255,255,255,.4)"; b.style.color="white"; }}
              onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor="rgba(255,255,255,.18)"; b.style.color="rgba(255,255,255,.55)"; }}>
              ← Back to Portfolio
            </button>
          </div>
        </div>
      </div>

      <div style={{ background:"#0a0a0a", padding:"24px 60px", display:"flex", justifyContent:"space-between", alignItems:"center", borderTop:"1px solid rgba(255,255,255,.05)" }} className="cs-topbar-pad">
        <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:14, color:"white", letterSpacing:-.3 }}>Lobna<span style={{ color:"#7dd4c0" }}>.</span></span>
        <span style={{ fontSize:12, color:"rgba(255,255,255,.25)" }}>Petling · Case Study · 2023–2026</span>
      </div>
    </div>
  );
}

// ── FITQUEST CASE STUDY PAGE ──────────────────────────────────────────────
function FitQuestCaseStudy() {
  const onBack = () => {
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const FIGMA_LINK = "https://www.figma.com/design/hz7DWdX0a78SAw4InE8CTz/FitQuest--Lobna-AbouHussein?node-id=0-1";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Teal palette for FitQuest
  const TEAL = "#1e8a8a";
  const TEAL_LIGHT = "#20b0b0";
  const TEAL_PALE = "#e0f5f5";
  const TEAL_DARK = "#0c4a4a";

  const sectionLabel = (num: string, text: string) => (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
      <div style={{ width:32, height:1.5, background:TEAL }} />
      <span style={{ fontSize:10, fontWeight:700, letterSpacing:2.5, textTransform:"uppercase", color:TEAL }}>
        {num} — {text}
      </span>
    </div>
  );

  const StatCard = ({ value, desc }: { value: string; desc: string }) => (
    <div style={{ background:TEAL_DARK, border:`1px solid rgba(32,176,176,.2)`, borderRadius:18, padding:"36px 28px", flex:1 }}>
      <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(40px,5vw,60px)", color:TEAL_LIGHT, lineHeight:1, letterSpacing:-2, marginBottom:14 }}>{value}</div>
      <p style={{ fontSize:13, color:"rgba(255,255,255,.5)", lineHeight:1.7, fontWeight:300 }}>{desc}</p>
    </div>
  );

  const DecisionCard = ({ title, body, dark }: { title: string; body: string; dark?: boolean }) => (
    <div style={{ background: dark ? TEAL_DARK : TEAL_PALE, border:`1px solid ${dark ? "rgba(32,176,176,.2)" : "rgba(30,138,138,.12)"}`, borderRadius:18, padding:"28px 24px" }}>
      <h4 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:15, color: dark ? TEAL_LIGHT : TEAL, marginBottom:10, lineHeight:1.3 }}>{title}</h4>
      <p style={{ fontSize:13, color: dark ? "rgba(255,255,255,.55)" : "#444", lineHeight:1.75, fontWeight:300 }}>{body}</p>
    </div>
  );

  const FlowStep = ({ num, title, desc }: { num: string; title: string; desc: string }) => (
    <div style={{ display:"flex", alignItems:"flex-start", gap:16 }}>
      <div style={{ width:36, height:36, borderRadius:"50%", background:TEAL, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:12, color:"white", flexShrink:0 }}>{num}</div>
      <div>
        <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:13, color:"#0e0e0e", marginBottom:4 }}>{title}</div>
        <div style={{ fontSize:12, color:"#666", lineHeight:1.65, fontWeight:300 }}>{desc}</div>
      </div>
    </div>
  );

  const LearningRow = ({ arrow, text }: { arrow: string; text: string }) => (
    <div style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px 0", borderBottom:"1px solid rgba(255,255,255,.05)" }}>
      <span style={{ color:TEAL_LIGHT, fontSize:14, flexShrink:0, marginTop:1 }}>{arrow}</span>
      <p style={{ fontSize:13, color:"rgba(255,255,255,.6)", lineHeight:1.7, fontWeight:300, margin:0 }}>{text}</p>
    </div>
  );

  const NextRow = ({ arrow, text }: { arrow: string; text: string }) => (
    <div style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px 0", borderBottom:"1px solid rgba(30,138,138,.1)" }}>
      <span style={{ color:TEAL, fontSize:14, flexShrink:0, marginTop:1 }}>{arrow}</span>
      <p style={{ fontSize:13, color:"#444", lineHeight:1.7, fontWeight:300, margin:0 }}>{text}</p>
    </div>
  );

  return (
    <div style={{ background:"#f8f7f4", minHeight:"100vh" }}>

      {/* ── STICKY TOP BAR ── */}
      <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:1000, background:"rgba(12,74,74,.97)", backdropFilter:"blur(14px)", borderBottom:`1px solid rgba(32,176,176,.15)`, height:64, display:"flex", alignItems:"center", padding:"0 60px", justifyContent:"space-between" }} className="cs-topbar-pad">
        <button onClick={onBack} data-hover
          style={{ display:"flex", alignItems:"center", gap:8, background:"none", border:"none", cursor:"none", fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:13, color:"rgba(255,255,255,.6)", padding:0, transition:"color .2s" }}
          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "white"}
          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,.6)"}>
          ← Back to Portfolio
        </button>
        <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:16, letterSpacing:-.5, color:"white" }}>
          Lobna<span style={{ color:TEAL_LIGHT }}>.</span>
        </span>
        <a href={FIGMA_LINK} target="_blank" rel="noreferrer"
          style={{ background:TEAL_LIGHT, color:"#0c2a2a", padding:"9px 22px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:12, display:"flex", alignItems:"center", gap:6, transition:"all .25s", textDecoration:"none" }}
          onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background="white"; t.style.color=TEAL_DARK; }}
          onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background=TEAL_LIGHT; t.style.color="#0c2a2a"; }}>
          View in Figma ↗︎
        </a>
      </div>

      {/* ── HERO ── */}
      <div style={{ paddingTop:64, background:TEAL_DARK, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-120, right:-120, width:500, height:500, background:TEAL_LIGHT, borderRadius:"50%", filter:"blur(120px)", opacity:.12, pointerEvents:"none" }} />
        <div style={{ position:"absolute", bottom:-80, left:-80, width:400, height:400, background:"#1e4a4a", borderRadius:"50%", filter:"blur(80px)", opacity:.5, pointerEvents:"none" }} />
        {/* noise texture */}
        <div style={{ position:"absolute", inset:0, backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")", pointerEvents:"none", opacity:.3 }} />

        <div style={{ maxWidth:1200, margin:"0 auto", padding:"80px 60px 0", position:"relative", zIndex:1 }} className="cs-section-pad">
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:32 }}>
            <div style={{ background:"rgba(32,176,176,.15)", border:`1px solid rgba(32,176,176,.3)`, borderRadius:100, padding:"5px 14px", display:"inline-flex", alignItems:"center", gap:6 }}>
              <div style={{ width:6, height:6, background:TEAL_LIGHT, borderRadius:"50%" }} />
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL_LIGHT }}>UI/UX Case Study · Mobile App · 2025</span>
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"flex-end" }} className="cs-hero-grid">
            <div>
              <h1 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(56px,8vw,100px)", letterSpacing:-4, lineHeight:.88, color:"white", marginBottom:0 }}>
                FitQuest
              </h1>
              <h1 style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, fontSize:"clamp(44px,6vw,80px)", letterSpacing:-2, lineHeight:.95, color:TEAL_LIGHT, marginBottom:28 }}>
                Train & Grow.
              </h1>
              <p style={{ fontSize:17, color:"rgba(255,255,255,.65)", lineHeight:1.75, fontWeight:300, maxWidth:480, marginBottom:36 }}>
                A full-cycle fitness app designed to guide users from their very first tap through personalised onboarding, real-time workout sessions, and daily health tracking — all within one clean, motivating interface.
              </p>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:48 }}>
                {["UI/UX Design","Gamification","Mobile App","iOS · Android","Figma"].map((tag, i) => (
                  <span key={i} style={{ background: i===0 ? TEAL_LIGHT : "transparent", color: i===0 ? TEAL_DARK : TEAL_LIGHT, border:"1.5px solid", borderColor: i===0 ? TEAL_LIGHT : "rgba(32,176,176,.35)", borderRadius:100, padding:"6px 16px", fontSize:11, fontWeight:700, fontFamily:"'Cabinet Grotesk',sans-serif", letterSpacing:.3 }}>
                    {tag}
                  </span>
                ))}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:24, paddingTop:36, borderTop:"1px solid rgba(255,255,255,.1)" }}>
                {[{label:"Role",val:"UI/UX Designer"},{label:"Year",val:"2025"},{label:"Platform",val:"iOS · Android"},{label:"Tool",val:"Figma"}].map((m,i) => (
                  <div key={i}>
                    <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"rgba(255,255,255,.3)", marginBottom:4 }}>{m.label}</div>
                    <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:13, color:"white" }}>{m.val}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"flex-end" }}>
              <img
                src="https://i.ibb.co/q30cyyct/760929219651445-67b5b168a5b5d.png"
                alt="FitQuest App"
                style={{ width:"100%", maxWidth:520, borderRadius:"24px 24px 0 0", objectFit:"cover", display:"block" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── 01 OVERVIEW ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("01", "Overview")}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:80, alignItems:"center" }} className="cs-two-col">
            <div>
              <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:24 }}>
                A smarter way to{" "}
                <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL }}>train & grow.</em>
              </h2>
              <p style={{ fontSize:15, color:"#555", lineHeight:1.85, fontWeight:300, marginBottom:24 }}>
                FitQuest is a full-cycle fitness app designed to guide users from their very first tap through personalised onboarding, real-time workout sessions, and daily health tracking — all within one clean, motivating interface.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:28 }}>
                {[
                  { icon:"🎯", label:"Challenge", desc:"Most fitness apps overwhelm beginners or bore experienced athletes. FitQuest needed to serve both by adapting its experience to each user's profile and goal from day one." },
                  { icon:"⚡", label:"Solution", desc:"A structured onboarding questionnaire feeds a personalised dashboard and workout system — with live tracking, rest timers, performance metrics, social groups, and post-workout celebrations." },
                  { icon:"🏆", label:"Outcome", desc:"A cohesive 3-flow system — Sign-Up, Dashboard & Profile, Workout Details — that feels intuitive on day one yet powerful enough to keep athletes returning every day." },
                ].map((item, i) => (
                  <div key={i} style={{ display:"flex", gap:14, padding:"16px 18px", background:"white", borderRadius:14, border:"1px solid rgba(0,0,0,.07)", boxShadow:"0 2px 10px rgba(0,0,0,.04)" }}>
                    <div style={{ width:38, height:38, borderRadius:10, background:TEAL_PALE, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{item.icon}</div>
                    <div>
                      <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL, marginBottom:4 }}>{item.label}</div>
                      <div style={{ fontSize:12, color:"#555", lineHeight:1.65, fontWeight:300 }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background:TEAL_DARK, borderRadius:24, padding:"48px 36px", display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"rgba(255,255,255,.35)", marginBottom:8 }}>Core Design Principle</div>
              <p style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontSize:22, lineHeight:1.55, color:"white" }}>
                "Every screen was crafted with a singular purpose — reduce friction, celebrate progress, and keep the user coming back day after day."
              </p>
              <div style={{ marginTop:16, display:"flex", gap:8, flexWrap:"wrap" }}>
                {["Personalisation","Progressive Disclosure","Gamification","Motivation Psychology"].map((tag,i) => (
                  <span key={i} style={{ background:"rgba(32,176,176,.12)", color:TEAL_LIGHT, border:`1px solid rgba(32,176,176,.25)`, borderRadius:100, padding:"4px 12px", fontSize:10, fontWeight:700, fontFamily:"'Cabinet Grotesk',sans-serif" }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 02 PROBLEM SPACE ── */}
      <div style={{ background:TEAL_DARK, padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("02", "Problem Space")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(32px,4.5vw,56px)", letterSpacing:-2, lineHeight:.95, color:"white", marginBottom:12 }}>
            Why another{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL_LIGHT }}>fitness app?</em>
          </h2>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.5)", lineHeight:1.8, maxWidth:560, marginBottom:56, fontWeight:300 }}>
            The global fitness app market is crowded — yet user retention remains shockingly low. The problem isn't features. It's friction, overwhelm, and zero personalisation from the very first screen.
          </p>
          <div style={{ display:"flex", gap:16 }} className="cs-three-col">
            <StatCard value="77%" desc="of fitness app users abandon within 3 days — almost always due to a confusing or impersonal onboarding experience." />
            <StatCard value="3×" desc="higher retention when users complete a personalised setup questionnaire on their very first session." />
            <StatCard value="68%" desc="of users prefer apps that adapt to their fitness level rather than offering a one-size-fits-all plan." />
          </div>
        </div>
      </div>

      {/* ── 03 THE SCREENS – FLOW 01 ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("03", "The Screens")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:8 }}>
            Three flows.{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL }}>One cohesive journey.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:580, marginBottom:52, fontWeight:300 }}>
            Every screen was crafted with a singular purpose — reduce friction, celebrate progress, and keep the user coming back day after day.
          </p>

          {/* Flow 01 */}
          <div style={{ marginBottom:64 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
              <span style={{ background:TEAL, color:"white", fontSize:9, fontWeight:800, letterSpacing:1.5, textTransform:"uppercase", padding:"4px 12px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif" }}>Flow 01</span>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL }}>Sign-Up Questionnaire</span>
            </div>
            <p style={{ fontSize:14, color:"#555", lineHeight:1.8, maxWidth:700, marginBottom:36, fontWeight:300 }}>
              One question per screen on a bold teal canvas. The scroll-wheel inputs for age, height, and weight eliminate keyboard friction entirely — onboarding feels native and effortless from the first screen. The full sequence: Gender → Age → Height → Weight → Personal Data Log → Fill Profile.
            </p>

            {/* Steps left, image right */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, alignItems:"center", marginBottom:32 }} className="cs-two-col">
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <FlowStep num="01" title="Gender" desc="Large selectable icons — clear, accessible, immediate." />
                <FlowStep num="02" title="Age · Height · Weight" desc="Native scroll-wheel selectors. One gesture, one value, one Continue tap." />
                <FlowStep num="03" title="Personal Data Log" desc="Optional health history entry to personalise recommendations." />
                <FlowStep num="04" title="Goal & Activity" desc="Sets the user's primary objective and current fitness level." />
                <FlowStep num="05" title="Fill Profile" desc="Avatar, name, and social handles to activate community features." />
              </div>
              <div style={{ borderRadius:20, overflow:"hidden", boxShadow:`0 20px 60px rgba(12,74,74,.15)`, border:`1px solid rgba(30,138,138,.1)` }}>
                <img src="https://i.ibb.co/bjWb3qPB/548f35219651445-67b5b168a68ae.png" alt="FitQuest onboarding screens" style={{ width:"100%", display:"block" }} />
              </div>
            </div>

            {/* Full-width second onboarding image */}
            <div style={{ borderRadius:20, overflow:"hidden", boxShadow:`0 20px 60px rgba(12,74,74,.12)`, border:`1px solid rgba(30,138,138,.08)` }}>
              <img src="https://i.ibb.co/h179qKNd/13700c219651445-67b5b168a4dfe.png" alt="FitQuest full onboarding sequence" style={{ width:"100%", display:"block" }} />
            </div>
            <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"center", flexWrap:"wrap" }}>
              {["Gender","Age","Height","Weight","Personal Data Log","Fill Profile"].map((label,i) => (
                <span key={i} style={{ background:"white", border:"1px solid rgba(0,0,0,.08)", borderRadius:100, padding:"5px 14px", fontSize:10, fontWeight:600, color:"#555", fontFamily:"'Cabinet Grotesk',sans-serif", letterSpacing:.3 }}>{label}</span>
              ))}
            </div>
          </div>

          {/* Flow 02 */}
          <div style={{ marginBottom:64 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
              <span style={{ background:TEAL, color:"white", fontSize:9, fontWeight:800, letterSpacing:1.5, textTransform:"uppercase", padding:"4px 12px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif" }}>Flow 02</span>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL }}>Profile & Dashboard</span>
            </div>
            <p style={{ fontSize:14, color:"#555", lineHeight:1.8, maxWidth:560, marginBottom:32, fontWeight:300 }}>
              The home screen surfaces the user's avatar, stats, group memberships, 7-day streak rings, and today's health data in one scrollable view. An animated avatar beside the real user photo creates personality and warmth without sacrificing credibility — a signal that this app will be motivating, not clinical.
            </p>
            <div style={{ borderRadius:20, overflow:"hidden", boxShadow:`0 16px 48px rgba(12,74,74,.15)`, border:`1px solid rgba(30,138,138,.1)` }}>
              <img src="https://i.ibb.co/XkKRQ71J/43f22a219651445-67b5b168a6232.png" alt="FitQuest profile and dashboard" style={{ width:"100%", display:"block" }} />
            </div>
            <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"center" }}>
              {["Profile Overview","Dashboard + Workout Tracking"].map((label,i) => (
                <span key={i} style={{ background:"white", border:"1px solid rgba(0,0,0,.08)", borderRadius:100, padding:"5px 14px", fontSize:10, fontWeight:600, color:"#555", fontFamily:"'Cabinet Grotesk',sans-serif", letterSpacing:.3 }}>{label}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── FLOW 03 – WORKOUT DETAILS ── */}
      <div style={{ background:TEAL_DARK, padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
            <span style={{ background:TEAL_LIGHT, color:TEAL_DARK, fontSize:9, fontWeight:800, letterSpacing:1.5, textTransform:"uppercase", padding:"4px 12px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif" }}>Flow 03</span>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL_LIGHT }}>Workout Details</span>
          </div>
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,44px)", letterSpacing:-1.5, lineHeight:1, color:"white", marginBottom:16 }}>
            Everything you need{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL_LIGHT }}>mid-session.</em>
          </h2>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.5)", lineHeight:1.8, maxWidth:560, marginBottom:48, fontWeight:300 }}>
            The Details and Tracking tabs give athletes everything mid-session — video preview, exercise description, rep counts, calorie targets, and live performance tracking. Three tabs, three states, instantly readable.
          </p>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:48 }} className="cs-three-col">
            {[
              { tag:"Tracking Tab", desc:"Animated exercise illustration + real-time elapsed clock + equipment/calorie/rep metadata icons for instant context during a set." },
              { tag:"Timer Tab", desc:"Circular progress ring with digital display. White bg = active set. Teal fill = REST — users know their state the instant they glance down." },
              { tag:"Completion Screen", desc:"Medal icon, star rating, exercises, calories, points, time, and review — closes the loop and creates a shareable celebratory moment." },
            ].map((item,i) => (
              <div key={i} style={{ background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.08)", borderRadius:16, padding:"24px 20px" }}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL_LIGHT, marginBottom:10 }}>{item.tag}</div>
                <p style={{ fontSize:12, color:"rgba(255,255,255,.5)", lineHeight:1.7, fontWeight:300 }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:40 }}>
            <div style={{ borderRadius:20, overflow:"hidden", boxShadow:`0 20px 60px rgba(0,0,0,.35)` }}>
              <img src="https://i.ibb.co/rRH8q9M4/b8b7fe219651445-67b5b168a6c98.png" alt="FitQuest workout details tab" style={{ width:"100%", display:"block" }} />
            </div>
            <div style={{ borderRadius:20, overflow:"hidden", boxShadow:`0 20px 60px rgba(0,0,0,.35)` }}>
              <img src="https://i.ibb.co/KpJbfnfW/5d0b5c219651445-67b5b168a7330.png" alt="FitQuest workout tracking tab" style={{ width:"100%", display:"block" }} />
            </div>
          </div>

          <div style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)", borderRadius:20, padding:"36px 40px" }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:TEAL_LIGHT, marginBottom:14 }}>Design Decision — REST vs. Active Timer States</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:40, alignItems:"center" }} className="cs-two-col">
              <div>
                <h3 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(18px,2.5vw,28px)", color:"white", lineHeight:1.1, marginBottom:16 }}>
                  Color communicates state{" "}
                  <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL_LIGHT }}>faster than any label.</em>
                </h3>
                <p style={{ fontSize:13, color:"rgba(255,255,255,.5)", lineHeight:1.8, fontWeight:300 }}>
                  Swapping the full background to teal during REST communicates the change of state faster than any icon or label could. Athletes mid-exhaustion need information in under a second — color delivers it. This single decision removed the need for a dedicated REST indicator element entirely.
                </p>
              </div>
              <div style={{ display:"flex", gap:12 }}>
                <div style={{ flex:1, background:"rgba(32,176,176,.15)", border:`1px solid rgba(32,176,176,.3)`, borderRadius:14, padding:"20px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.2, textTransform:"uppercase", color:TEAL_LIGHT, marginBottom:8 }}>Active State</div>
                  <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:28, color:"white", lineHeight:1 }}>32:10</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,.3)", marginTop:6 }}>White background</div>
                </div>
                <div style={{ flex:1, background:TEAL_LIGHT, borderRadius:14, padding:"20px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.2, textTransform:"uppercase", color:TEAL_DARK, marginBottom:8 }}>REST State</div>
                  <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:28, color:TEAL_DARK, lineHeight:1 }}>32:10</div>
                  <div style={{ fontSize:10, color:TEAL_DARK, opacity:.6, marginTop:6 }}>Teal fill = REST</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 04 USER FLOW ── */}
      <div style={{ background:"white", padding:"80px 60px", borderTop:"1px solid rgba(0,0,0,.06)" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("04", "User Flow")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:10 }}>
            From stranger{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL }}>to daily athlete.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:560, marginBottom:48, fontWeight:300 }}>
            Every screen connects to the next with clear intention. Here's the complete path a new user takes through FitQuest.
          </p>

          {/* Onboarding flow */}
          <div style={{ marginBottom:32 }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL, marginBottom:16 }}>Onboarding Path</div>
            <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:8 }}>
              {["Launch · Splash","Gender · Step 1","Age · Step 2","Height · Step 3","Weight · Step 4","Data Log · Step 5","Goal · Step 6","Activity · Step 7","Profile · Step 8"].map((step, i, arr) => (
                <React.Fragment key={i}>
                  <div style={{ background:TEAL_PALE, border:`1px solid rgba(30,138,138,.2)`, borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
                    <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:11, color:"#0e0e0e" }}>{step.split(" · ")[0]}</div>
                    {step.split(" · ")[1] && <div style={{ fontSize:9, color:TEAL, fontWeight:600 }}>{step.split(" · ")[1]}</div>}
                  </div>
                  {i < arr.length - 1 && <span style={{ color:"rgba(0,0,0,.2)", fontSize:12 }}>→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Workout flow */}
          <div style={{ background:"#0e0e0e", borderRadius:18, padding:"32px 28px" }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL_LIGHT, marginBottom:16 }}>Workout Path</div>
            <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:8 }}>
              {["Dashboard · Home","Select Workout · Browse","Exercise Details · Review","Live Session · Timer","REST · Recovery","Complete ★ · Summary"].map((step, i, arr) => (
                <React.Fragment key={i}>
                  <div style={{ background: i===arr.length-1 ? TEAL : "rgba(255,255,255,.06)", border:`1px solid ${i===arr.length-1 ? TEAL_LIGHT : "rgba(255,255,255,.1)"}`, borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
                    <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:11, color: i===arr.length-1 ? "white" : "rgba(255,255,255,.75)" }}>{step.split(" · ")[0]}</div>
                    {step.split(" · ")[1] && <div style={{ fontSize:9, color: i===arr.length-1 ? TEAL_LIGHT : "rgba(255,255,255,.3)", fontWeight:600 }}>{step.split(" · ")[1]}</div>}
                  </div>
                  {i < arr.length - 1 && <span style={{ color:"rgba(255,255,255,.2)", fontSize:12 }}>→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── 05 DESIGN DECISIONS ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("05", "Design Decisions")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:12 }}>
            Every choice{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL }}>has a reason.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:560, marginBottom:48, fontWeight:300 }}>
            The visual and interaction decisions in FitQuest weren't purely aesthetic — each one serves a clear, measurable UX purpose.
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }} className="cs-two-col">
            <DecisionCard dark title="Teal as the Brand Color" body="Teal sits at the intersection of calm (blue) and vitality (green) — psychologically aligned with health, trust, and sustained effort. It avoids aggressive gym-red while feeling energetic across both dark and light contexts." />
            <DecisionCard title="Scroll-Wheel Inputs" body="For age, weight, and height, native scroll-wheel selectors eliminate keyboard friction entirely. One gesture, one value, one Continue tap — onboarding feels native and effortless from the first screen." />
            <DecisionCard title="Animated Avatar on Profile" body="The illustrated character beside the real user photo creates personality and warmth without sacrificing credibility. A subtle signal: this app will be motivating, not clinical." />
            <DecisionCard dark title="REST vs. Active Timer States" body="Swapping the full background to teal during REST communicates the change of state faster than any icon or label could. Athletes mid-exhaustion need information in under a second — color delivers it." />
          </div>
          {/* Retention loop — full width */}
          <div style={{ background:TEAL, borderRadius:18, padding:"32px 36px" }}>
            <h4 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:17, color:"white", marginBottom:10 }}>Post-Workout Celebration = The Retention Loop</h4>
            <p style={{ fontSize:14, color:"rgba(255,255,255,.75)", lineHeight:1.8, fontWeight:300 }}>
              The summary screen — medal icon, star rating, calories, points, time elapsed, review, and photo upload — is intentionally celebratory. Finishing a workout should feel like winning a trophy. This screen closes the psychological loop, creates a shareable social moment, and dramatically increases the probability the user returns tomorrow.
            </p>
          </div>
        </div>
      </div>

      {/* ── 06 VISUAL IDENTITY ── */}
      <div style={{ background:TEAL_DARK, padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("06", "Visual Identity")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"white", marginBottom:12 }}>
            Color{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL_LIGHT }}>&amp; Typography.</em>
          </h2>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.45)", lineHeight:1.8, maxWidth:560, marginBottom:48, fontWeight:300 }}>
            A restrained teal spectrum built for clarity, accessibility, and motivation. Two typefaces carry all the personality — one for impact, one for precision.
          </p>

          {/* Color palette */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:12, marginBottom:48 }} className="cs-color-grid">
            {[
              { hex:"#0C2E2E", name:"Deep Teal" },
              { hex:"#155F5F", name:"Dark Teal" },
              { hex:"#1E8A8A", name:"Primary Teal" },
              { hex:"#20B0B0", name:"Mid Teal" },
              { hex:"#5DE8D8", name:"Light Teal" },
              { hex:"#E8F8F8", name:"Pale Teal" },
              { hex:"#F5B731", name:"Star Gold" },
            ].map((c,i) => (
              <div key={i}>
                <div style={{ height:60, borderRadius:12, background:c.hex, marginBottom:8, border:"1px solid rgba(255,255,255,.08)" }} />
                <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,.6)", fontFamily:"'Cabinet Grotesk',sans-serif" }}>{c.name}</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,.3)" }}>{c.hex}</div>
              </div>
            ))}
          </div>

          {/* Typography */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)", borderRadius:18, padding:"32px 28px" }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"rgba(255,255,255,.3)", marginBottom:12 }}>Display / Headlines</div>
              <div style={{ fontFamily:"'Instrument Serif', serif", fontWeight:400, fontSize:42, color:"white", lineHeight:1, marginBottom:12 }}>FitQuest</div>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL_LIGHT, marginBottom:8 }}>Playfair Display</div>
              <p style={{ fontSize:12, color:"rgba(255,255,255,.4)", lineHeight:1.65, fontWeight:300 }}>Elegant yet bold. Used for hero text, section titles, and large numerals. Brings editorial authority and timeless energy to the fitness context.</p>
            </div>
            <div style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)", borderRadius:18, padding:"32px 28px" }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"rgba(255,255,255,.3)", marginBottom:12 }}>Body / UI Copy</div>
              <div style={{ fontSize:18, color:"white", lineHeight:1.5, marginBottom:12, fontWeight:400 }}>Track every rep.<br />Celebrate every win.</div>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:TEAL_LIGHT, marginBottom:8 }}>Plus Jakarta Sans</div>
              <p style={{ fontSize:12, color:"rgba(255,255,255,.4)", lineHeight:1.65, fontWeight:300 }}>Geometric and highly legible at small sizes. Used for all UI labels, descriptions, and captions. Pairs naturally with Playfair Display.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── 07 DESIGN PROCESS ── */}
      <div style={{ background:"white", padding:"80px 60px", borderTop:"1px solid rgba(0,0,0,.06)", borderBottom:"1px solid rgba(0,0,0,.06)" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("07", "Design Process")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:10 }}>
            How we got{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL }}>here.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:540, marginBottom:52, fontWeight:300 }}>
            A structured four-phase approach ensured every decision was grounded in real user needs — from first research interview to final pixel.
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
            {[
              { num:"01", title:"Research & Discovery", desc:"Competitive analysis of Nike Training Club, MyFitnessPal, and Freeletics. User interviews to identify core pain points: onboarding friction, data overload, and absent progression feedback." },
              { num:"02", title:"Define & Structure", desc:"User personas, journey maps, and information architecture. Identified three primary flows — onboarding, daily tracking, and workout sessions — as the product backbone." },
              { num:"03", title:"Wireframing & Testing", desc:"Low-fidelity wireframes tested with target users. Key learning: one question per onboarding screen, all daily stats on the home screen, dedicated timer mode for live sessions." },
              { num:"04", title:"High-Fidelity Design", desc:"Full design system in Figma — components, color tokens, text styles, and icons. Every screen designed for accessibility and consistency across both teal and white contexts." },
            ].map((s,i) => (
              <div key={i} style={{ border:`1px solid rgba(0,0,0,.08)`, borderRadius:18, padding:"28px 24px", borderTop:`3px solid ${TEAL}` }}>
                <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontSize:44, fontWeight:900, color:"rgba(0,0,0,.04)", lineHeight:1, marginBottom:16, letterSpacing:-2 }}>{s.num}</div>
                <h3 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:16, color:"#0e0e0e", marginBottom:10 }}>{s.title}</h3>
                <p style={{ fontSize:13, color:"#555", lineHeight:1.75, fontWeight:300 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 08 OUTCOMES ── */}
      <div style={{ background:TEAL, padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("08", "Outcomes")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"white", marginBottom:12 }}>
            Results that{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"rgba(255,255,255,.7)" }}>matter.</em>
          </h2>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.7)", lineHeight:1.8, maxWidth:540, marginBottom:52, fontWeight:300 }}>
            FitQuest's design was validated through usability testing and prototype walkthroughs. The numbers reflect the power of a user-centred approach.
          </p>
          <div style={{ display:"flex", gap:16 }} className="cs-three-col">
            {[
              { value:"8", desc:"Onboarding screens that feel like 3 — users completed setup in under 2 minutes on average." },
              { value:"94%", desc:"Task completion rate across all three major flows during usability testing." },
              { value:"4.8★", desc:"Average design satisfaction score from prototype feedback participants." },
            ].map((s,i) => (
              <div key={i} style={{ background:"rgba(0,0,0,.15)", border:"1px solid rgba(255,255,255,.2)", borderRadius:18, padding:"36px 28px", flex:1 }}>
                <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(40px,5vw,60px)", color:"white", lineHeight:1, letterSpacing:-2, marginBottom:14 }}>{s.value}</div>
                <p style={{ fontSize:13, color:"rgba(255,255,255,.7)", lineHeight:1.7, fontWeight:300 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 09 REFLECTION ── */}
      <div style={{ background:"#f8f7f4", padding:"80px 60px" }} className="cs-section-pad">
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {sectionLabel("09", "Reflection")}
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(28px,4vw,48px)", letterSpacing:-1.5, lineHeight:1, color:"#0e0e0e", marginBottom:10 }}>
            What I learned{" "}
            <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL }}>building this.</em>
          </h2>
          <p style={{ fontSize:14, color:"#666", lineHeight:1.8, maxWidth:560, marginBottom:52, fontWeight:300 }}>
            FitQuest pushed me to think deeply about motivation psychology, progressive disclosure, and balancing data richness with cognitive load.
          </p>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:40 }} className="cs-two-col">
            <div>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:TEAL, marginBottom:20 }}>Key Learnings</div>
              <div style={{ background:TEAL_DARK, borderRadius:18, padding:"24px 28px" }}>
                <LearningRow arrow="→" text="Progressive disclosure in onboarding dramatically improves completion — one question per screen, always." />
                <LearningRow arrow="→" text="Color alone can communicate workout state changes faster than any icon or label ever could." />
                <LearningRow arrow="→" text="Gamification works only when genuinely earned — design for real achievement, not artificial rewards." />
                <LearningRow arrow="→" text="Social features like groups and leaderboards are powerful retention drivers that deserve prime real estate." />
              </div>
            </div>
            <div>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:TEAL, marginBottom:20 }}>What's Next</div>
              <div style={{ background:"white", border:"1px solid rgba(0,0,0,.08)", borderRadius:18, padding:"24px 28px", boxShadow:"0 4px 24px rgba(0,0,0,.05)" }}>
                <NextRow arrow="→" text="Design the Nutrition flow — meal logging, macro breakdown, and calorie goal visualisation." />
                <NextRow arrow="→" text="Build the Explore screen — workout discovery and curated programs by muscle group." />
                <NextRow arrow="→" text="Develop the Arena & Grandstand tabs — competitive challenges and community spectating." />
                <NextRow arrow="→" text="Run A/B testing on the post-workout summary to optimise for sharing and repeat sessions." />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{ background:TEAL_DARK, padding:"80px 60px", textAlign:"center" }} className="cs-section-pad">
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:44, color:"white", letterSpacing:-2, lineHeight:.95, marginBottom:16 }}>
            See it in<br /><em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:TEAL_LIGHT }}>Figma.</em>
          </div>
          <p style={{ fontSize:14, color:"rgba(255,255,255,.45)", lineHeight:1.8, marginBottom:32, fontWeight:300 }}>
            Explore the full prototype, design system, and every screen in the Figma file.
          </p>
          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <a href={FIGMA_LINK} target="_blank" rel="noreferrer"
              style={{ background:TEAL_LIGHT, color:TEAL_DARK, padding:"14px 32px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:14, display:"inline-flex", alignItems:"center", gap:8, transition:"all .25s", textDecoration:"none" }}
              onMouseEnter={e => { const t=e.currentTarget as HTMLAnchorElement; t.style.transform="translateY(-3px)"; t.style.boxShadow=`0 12px 36px rgba(32,176,176,.4)`; }}
              onMouseLeave={e => { const t=e.currentTarget as HTMLAnchorElement; t.style.transform="none"; t.style.boxShadow="none"; }}>
              Open in Figma ↗︎
            </a>
            <button onClick={onBack} data-hover
              style={{ background:"transparent", color:"rgba(255,255,255,.55)", padding:"14px 32px", borderRadius:100, fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:14, border:"1.5px solid rgba(255,255,255,.18)", cursor:"none", transition:"all .25s" }}
              onMouseEnter={e => { const b=e.currentTarget as HTMLButtonElement; b.style.borderColor="rgba(255,255,255,.4)"; b.style.color="white"; }}
              onMouseLeave={e => { const b=e.currentTarget as HTMLButtonElement; b.style.borderColor="rgba(255,255,255,.18)"; b.style.color="rgba(255,255,255,.55)"; }}>
              ← Back to Portfolio
            </button>
          </div>
        </div>
      </div>

      <div style={{ background:"#070f0f", padding:"24px 60px", display:"flex", justifyContent:"space-between", alignItems:"center", borderTop:`1px solid rgba(32,176,176,.1)` }} className="cs-topbar-pad">
        <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:14, color:"white", letterSpacing:-.3 }}>Lobna<span style={{ color:TEAL_LIGHT }}>.</span></span>
        <span style={{ fontSize:12, color:"rgba(255,255,255,.25)" }}>FitQuest · Case Study · 2025</span>
      </div>
    </div>
  );
}

// ── MAIN APP ───────────────────────────────────────────────────────────────
export default function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [counters, setCounters] = useState([0, 0, 0]);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const isCaseStudyPawPal = currentPath === "/pawpal" || currentPath === "/petling";
  const isCaseStudyFitQuest = currentPath === "/fitquest";
  const petlingPage = getPetlingPageByPath(currentPath);
  const isCaseStudy = isCaseStudyPawPal || isCaseStudyFitQuest || petlingPage !== null;

  useEffect(() => {
    const onPop = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const title = petlingPage
      ? petlingPage.docTitle
      : isCaseStudyPawPal
      ? "PawPal Case Study — Lobna AbouHussein"
      : isCaseStudyFitQuest
      ? "FitQuest Case Study — Lobna AbouHussein"
      : "Lobna AbouHussein — Portfolio";
    document.title = title;
    const svgFav = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="22" fill="%230e0e0e"/><text x="16" y="80" font-size="78" font-family="Arial Black,Arial" font-weight="900" fill="%237dd4c0">L</text></svg>`;
    const fav = (document.querySelector("link[rel='icon']") || document.createElement("link")) as HTMLLinkElement;
    fav.rel = "icon"; fav.href = `data:image/svg+xml,${svgFav}`;
    document.head.appendChild(fav);
    if (!document.getElementById("lo-fonts")) {
      const link = document.createElement("link");
      link.id = "lo-fonts";
      link.href = "https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=Instrument+Serif:ital@0;1&family=Nunito:wght@300;400;500;600;700&display=swap";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
  }, [isCaseStudyPawPal, isCaseStudyFitQuest, petlingPage ? petlingPage.id : ""]);

  useEffect(() => {
    if (isCaseStudy) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [isCaseStudy]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (isCaseStudy) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("lo-visible"); }),
      { threshold: 0.1 }
    );
    const timer = setTimeout(() => {
      document.querySelectorAll(".lo-reveal,.lo-stagger").forEach(el => observer.observe(el));
    }, 80);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [isCaseStudy]);

  useEffect(() => {
    const targets = [5, 7, 4];
    const duration = 1800;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCounters(targets.map(t => Math.floor(t * ease)));
      if (p < 1) requestAnimationFrame(tick);
      else setCounters(targets);
    };
    const timer = setTimeout(() => requestAnimationFrame(tick), 500);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
  };

  const openCaseStudy = (id: string) => {
    track("case_study_open", { id });
    window.history.pushState({}, "", `/${id}`);
    setCurrentPath(`/${id}`);
  };

  const caseStudyStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=Instrument+Serif:ital@0;1&family=Nunito:wght@300;400;500;600;700&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}
    body{font-family:'Nunito',sans-serif;background:#f8f7f4;color:#0e0e0e;overflow-x:hidden;cursor:none;-webkit-font-smoothing:antialiased;}
    a{color:inherit;text-decoration:none;}
    img{max-width:100%;display:block;}
    @keyframes float{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-28px) scale(1.04)}}
    @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
    @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.45;transform:scale(1.5)}}
    .cs-section-pad { padding-left: 60px; padding-right: 60px; }
    .cs-topbar-pad { padding-left: 60px; padding-right: 60px; }
    @media(max-width:900px){
      .cs-section-pad { padding-left: 24px !important; padding-right: 24px !important; }
      .cs-topbar-pad { padding-left: 24px !important; padding-right: 24px !important; }
      .cs-hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
      .cs-two-col { grid-template-columns: 1fr !important; gap: 32px !important; }
      .cs-three-col { flex-direction: column !important; }
      .cs-four-col { grid-template-columns: 1fr 1fr !important; }
      .cs-process-row { flex-direction: column !important; gap: 24px !important; }
      .cs-hide-mobile { display: none !important; }
      .cs-color-grid { grid-template-columns: repeat(4,1fr) !important; }
    }
    @media(max-width:520px){
      .cs-four-col { grid-template-columns: 1fr !important; }
      .cs-color-grid { grid-template-columns: repeat(3,1fr) !important; }
    }
  `;

  // ── CASE STUDY ROUTES ──
  if (petlingPage) {
    return (
      <>
        <style>{caseStudyStyles}</style>
        <CustomCursor key={petlingPage.id} />
        <PetlingSuitePage key={petlingPage.id} page={petlingPage} />
      </>
    );
  }

  if (isCaseStudyPawPal) {
    return (
      <>
        <style>{caseStudyStyles}</style>
        <CustomCursor />
        <PetlingCaseStudy />
      </>
    );
  }

  if (isCaseStudyFitQuest) {
    return (
      <>
        <style>{caseStudyStyles}</style>
        <CustomCursor />
        <FitQuestCaseStudy />
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=Instrument+Serif:ital@0;1&family=Nunito:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        body{font-family:'Nunito',sans-serif;background:#f8f7f4;color:#0e0e0e;overflow-x:hidden;cursor:none;-webkit-font-smoothing:antialiased;}
        a{color:inherit;text-decoration:none;}
        img{max-width:100%;display:block;}

        .lo-reveal{opacity:0;transform:translateY(44px);transition:opacity 0.75s cubic-bezier(.16,1,.3,1),transform 0.75s cubic-bezier(.16,1,.3,1);}
        .lo-reveal.lo-visible{opacity:1;transform:none;}
        .lo-stagger>*{opacity:0;transform:translateY(32px);transition:opacity 0.65s cubic-bezier(.16,1,.3,1),transform 0.65s cubic-bezier(.16,1,.3,1);}
        .lo-stagger.lo-visible>*:nth-child(1){opacity:1;transform:none;transition-delay:0s}
        .lo-stagger.lo-visible>*:nth-child(2){opacity:1;transform:none;transition-delay:.09s}
        .lo-stagger.lo-visible>*:nth-child(3){opacity:1;transform:none;transition-delay:.18s}
        .lo-stagger.lo-visible>*:nth-child(4){opacity:1;transform:none;transition-delay:.27s}
        .lo-stagger.lo-visible>*:nth-child(5){opacity:1;transform:none;transition-delay:.36s}
        .lo-stagger.lo-visible>*:nth-child(6){opacity:1;transform:none;transition-delay:.45s}

        @keyframes float{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-28px) scale(1.04)}}
        @keyframes floatCard{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
        @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.45;transform:scale(1.5)}}
        @keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        @keyframes rotateSlow{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}

        .lo-btn-primary{background:#0e0e0e;color:#f8f7f4;padding:14px 32px;border-radius:100px;font-size:14px;font-weight:700;font-family:'Cabinet Grotesk',sans-serif;letter-spacing:.3px;transition:all .28s;display:inline-flex;align-items:center;gap:8px;border:none;cursor:none;}
        .lo-btn-primary:hover{background:#7dd4c0;color:#0e0e0e;transform:translateY(-3px);box-shadow:0 12px 32px rgba(125,212,192,.4);}
        .lo-btn-secondary{background:transparent;color:#0e0e0e;padding:14px 32px;border-radius:100px;font-size:14px;font-weight:600;font-family:'Cabinet Grotesk',sans-serif;border:1.5px solid rgba(0,0,0,.18);transition:all .28s;display:inline-flex;align-items:center;gap:8px;cursor:none;}
        .lo-btn-secondary:hover{border-color:#0e0e0e;transform:translateY(-3px);}

        .lo-nav-link{font-size:14px;font-weight:500;color:#555;background:none;border:none;cursor:none;font-family:'Nunito',sans-serif;padding:0;position:relative;letter-spacing:.3px;transition:color .2s;}
        .lo-nav-link::after{content:'';position:absolute;bottom:-4px;left:0;width:0;height:1.5px;background:#7dd4c0;transition:width .28s;}
        .lo-nav-link:hover{color:#0e0e0e;}
        .lo-nav-link:hover::after{width:100%;}

        .lo-skill-row{background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.08);border-radius:16px;padding:20px 24px;display:flex;align-items:center;gap:16px;transition:all .28s;cursor:default;}
        .lo-skill-row:hover{background:rgba(125,212,192,.1);border-color:rgba(125,212,192,.32);transform:translateX(7px);}

        .lo-step{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.07);border-radius:20px;padding:32px 28px;transition:all .3s;}
        .lo-step:hover{background:rgba(184,168,212,.1);border-color:rgba(184,168,212,.28);transform:translateY(-5px);}

        .lo-proj-list::-webkit-scrollbar{width:3px;}
        .lo-proj-list::-webkit-scrollbar-track{background:transparent;}
        .lo-proj-list::-webkit-scrollbar-thumb{background:rgba(125,212,192,.3);border-radius:4px;}

        @media(max-width:900px){
          .lo-hero-inner{grid-template-columns:1fr!important;}
          .lo-hero-right{display:none!important;}
          .lo-hero-sec{padding:100px 24px 60px!important;min-height:auto!important;}
          .lo-about-inner{grid-template-columns:1fr!important;gap:40px!important;}
          .lo-proj-split{grid-template-columns:1fr!important;height:auto!important;gap:12px!important;}
          .lo-proj-list{height:auto!important;max-height:220px!important;}
          .lo-proj-card{height:auto!important;}
          .lo-card-body{grid-template-columns:1fr!important;}
          .lo-card-body>div:first-child{height:200px!important;border-right:none!important;border-bottom:1px solid rgba(255,255,255,.06)!important;}
          .lo-card-text{height:300px!important;padding:20px!important;}
          .lo-card-foot{flex-direction:column!important;align-items:stretch!important;gap:12px!important;}
          .lo-card-btns{width:100%!important;}
          .lo-card-btns>button,.lo-card-btns>span{flex:1!important;justify-content:center!important;text-align:center!important;}
          .lo-process-grid{grid-template-columns:1fr 1fr!important;}
          .lo-generic-sec{padding:80px 24px!important;}
          .lo-footer{flex-direction:column!important;gap:12px!important;text-align:center!important;padding:32px 24px!important;}
          .lo-nav-inner{padding:16px 24px!important;}
          .lo-nav-desktop{display:none!important;}
          .lo-hamburger{display:flex!important;}
          .lo-contact-row{flex-direction:column!important;align-items:center!important;}
          .lo-stats-row{gap:20px!important;flex-wrap:wrap!important;}
          .lo-hero-actions{flex-direction:column!important;align-items:flex-start!important;}
          .lo-form-grid{grid-template-columns:1fr!important;}
          section#projects{padding:80px 0 48px!important;}
          section#projects>div{padding:0 24px!important;}
        }
        @media(max-width:520px){
          .lo-process-grid{grid-template-columns:1fr!important;}
          .lo-hero-name{font-size:52px!important;letter-spacing:-2px!important;}
        }
        @media(max-width:384px){
          .lo-card-text{height:352px!important;}
        }
      `}</style>

      <CustomCursor />

      {/* ── NAV ── */}
      <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:1000, backdropFilter:"blur(14px)", background: scrolled?"rgba(248,247,244,.97)":"rgba(248,247,244,.88)", borderBottom:"1px solid rgba(0,0,0,.06)", transition:"all .3s" }}>
        <div className="lo-nav-inner" style={{ maxWidth:1200, margin:"0 auto", padding:"18px 60px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:18, letterSpacing:-.5 }}>
            Lobna<span style={{ color:"#7dd4c0" }}>.</span>
          </span>
          <ul className="lo-nav-desktop" style={{ display:"flex", gap:40, listStyle:"none", alignItems:"center" }}>
            {[["projects","Work"],["about","About"],["process","Process"],["contact","Contact"]].map(([id,lbl]) => (
              <li key={id}><button className="lo-nav-link" onClick={()=>scrollTo(id)}>{lbl}</button></li>
            ))}
            <li>
              {/* ── RESUME BUTTON 1: Nav bar ── */}
              <a href={RESUME_LINK} target="_blank" rel="noreferrer" onClick={handleResumeClick}
                style={{ background:"#0e0e0e", color:"#f8f7f4", padding:"10px 24px", borderRadius:100, fontWeight:600, fontSize:13, fontFamily:"'Nunito',sans-serif", transition:"all .25s", display:"inline-flex", alignItems:"center", gap:6 }}
                onMouseEnter={e=>{const t=e.currentTarget;t.style.background="#7dd4c0";t.style.color="#0e0e0e";}}
                onMouseLeave={e=>{const t=e.currentTarget;t.style.background="#0e0e0e";t.style.color="#f8f7f4";}}>
                Resume ↗︎
              </a>
            </li>
          </ul>
          <button className="lo-hamburger" onClick={()=>setMobileMenuOpen(o=>!o)}
            style={{ display:"none", background:"none", border:"none", cursor:"none", fontSize:22, color:"#0e0e0e", alignItems:"center" }}>
            {mobileMenuOpen?"✕":"☰"}
          </button>
        </div>
        {mobileMenuOpen && (
          <div style={{ padding:"20px 24px", borderTop:"1px solid rgba(0,0,0,.07)", display:"flex", flexDirection:"column", gap:4, background:"#f8f7f4" }}>
            {[["projects","Work"],["about","About"],["process","Process"],["contact","Contact"]].map(([id,lbl]) => (
              <button key={id} onClick={()=>scrollTo(id)} style={{ background:"none", border:"none", textAlign:"left", fontSize:16, fontWeight:600, cursor:"none", padding:"10px 0", color:"#0e0e0e", fontFamily:"'Cabinet Grotesk',sans-serif", borderBottom:"1px solid rgba(0,0,0,.06)" }}>{lbl}</button>
            ))}
            {/* ── RESUME BUTTON 2: Mobile menu ── */}
            <a href={RESUME_LINK} target="_blank" rel="noreferrer" onClick={handleResumeClick} style={{ marginTop:8 }}>
              <button className="lo-btn-primary" style={{ width:"100%", justifyContent:"center" }}>Download Resume ↗︎</button>
            </a>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="lo-hero-sec" id="home" style={{ minHeight:"100vh", padding:"140px 60px 80px", position:"relative", overflow:"hidden", background:"#f8f7f4" }}>
        {[{w:420,h:420,bg:"#7dd4c0",top:-110,right:-110,delay:"0s"},{w:320,h:320,bg:"#b8a8d4",bottom:40,left:-90,delay:"3s"},{w:220,h:220,bg:"#a8c8e8",top:"42%",right:"28%",delay:"5.5s"}].map((b,i)=>(
          <div key={i} style={{ position:"absolute", width:b.w, height:b.h, background:b.bg, top:(b as any).top, bottom:(b as any).bottom, left:(b as any).left, right:(b as any).right, borderRadius:"50%", filter:"blur(72px)", opacity:.32, animation:`float 9s ease-in-out infinite ${b.delay}`, pointerEvents:"none" }} />
        ))}
        <div style={{ position:"absolute", right:60, bottom:60, width:120, height:120, border:"1.5px dashed rgba(125,212,192,.3)", borderRadius:"50%", animation:"rotateSlow 18s linear infinite", pointerEvents:"none" }} />
        <div style={{ position:"absolute", right:75, bottom:75, width:90, height:90, border:"1px dashed rgba(125,212,192,.15)", borderRadius:"50%", animation:"rotateSlow 12s linear infinite reverse", pointerEvents:"none" }} />

        <div className="lo-hero-inner" style={{ maxWidth:1200, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center", position:"relative", zIndex:2 }}>
          <div>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#e0f5f0", border:"1px solid rgba(125,212,192,.4)", color:"#2a9d8a", fontSize:12, fontWeight:600, padding:"6px 14px", borderRadius:100, marginBottom:28, letterSpacing:.5, animation:"fadeUp .6s ease both" }}>
              <span style={{ width:7, height:7, background:"#2a9d8a", borderRadius:"50%", animation:"pulse 2s ease infinite", display:"inline-block", flexShrink:0 }} />
              Available for new opportunities
            </div>
            <h1 className="lo-hero-name" style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(52px,7vw,86px)", lineHeight:.92, letterSpacing:-3, color:"#0e0e0e", animation:"fadeUp .7s ease .1s both" }}>
              Lobna's<br />
              <em style={{ fontStyle:"normal", fontFamily:"'Instrument Serif',serif", fontWeight:400, color:"#7dd4c0", display:"block" }}>Portfolio.</em>
            </h1>
            <p style={{ marginTop:28, fontSize:17, lineHeight:1.78, color:"#555", fontWeight:300, maxWidth:480, animation:"fadeUp .7s ease .2s both" }}>
              I'm <strong style={{ color:"#0e0e0e", fontWeight:600 }}>Lobna AbouHussein</strong> — I turn complexity into clarity. From sports super-apps with 7+ personas to supply chain systems, I design experiences that make hard things feel <strong style={{ color:"#0e0e0e", fontWeight:600 }}>effortless</strong>.
            </p>
            <div className="lo-hero-actions" style={{ marginTop:40, display:"flex", gap:14, flexWrap:"wrap", animation:"fadeUp .7s ease .3s both" }}>
              <button className="lo-btn-primary" onClick={()=>scrollTo("projects")}>View My Work →</button>
              <button className="lo-btn-secondary" onClick={()=>scrollTo("contact")}>Say Hello</button>
              {/* ── RESUME BUTTON 3: Hero section ── */}
              <a href={RESUME_LINK} target="_blank" rel="noreferrer" onClick={handleResumeClick} className="lo-btn-secondary" style={{ textDecoration:"none" }}>Download Resume ↗︎</a>
            </div>
            <div className="lo-stats-row" style={{ display:"flex", gap:40, marginTop:56, animation:"fadeUp .7s ease .4s both" }}>
              {[["Years Experience",0],["Personas Designed For",1],["Industries",2]].map(([lbl,idx])=>(
                <div key={lbl as string}>
                  <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontSize:38, fontWeight:900, lineHeight:1, letterSpacing:-1 }}>
                    {counters[idx as number]}<span style={{ color:"#7dd4c0" }}>+</span>
                  </div>
                  <div style={{ fontSize:12, color:"#999", fontWeight:500, marginTop:4, letterSpacing:.4 }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lo-hero-right" style={{ position:"relative", height:500 }}>
            {[
              { top:40,  left:20,  w:220, bg:"#e0f5f0", emoji:"🏃", label:"Sports & Fitness", title:"Build-Up",  sub:"Super App · 7+ Personas", delay:"0s",  rot:"rotate(-3deg)" },
              { top:120, right:30, w:205, bg:"#f0ecf8", emoji:"🐾", label:"Pet Care",          title:"Petling",   sub:"Live · iOS & Android",  delay:"1s",  rot:"rotate(2deg)"  },
              { bottom:60,left:60, w:185, bg:"#e8f2fa", emoji:"👥", label:"HR Tech",            title:"Shift",    sub:"Web & Mobile App",         delay:"2s",  rot:"rotate(-1.5deg)"},
              { bottom:80,right:55,w:168, bg:"#e0f5f5", emoji:"💪", label:"Fitness",            title:"FitQuest", sub:"Gamified Workout App",      delay:".5s", rot:"rotate(2.5deg)"},
            ].map((fc,i)=>(
              <div key={i} style={{ position:"absolute", width:fc.w, background:`linear-gradient(135deg,${fc.bg},${fc.bg}bb)`, borderRadius:20, padding:20, boxShadow:"0 20px 60px rgba(0,0,0,.1)", transform:fc.rot, animation:`floatCard ${6+i}s ease-in-out infinite ${fc.delay}`, top:(fc as any).top, left:(fc as any).left, right:(fc as any).right, bottom:(fc as any).bottom }}>
                <div style={{ fontSize:18, marginBottom:10 }}>{fc.emoji}</div>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:"#999", marginBottom:6 }}>{fc.label}</div>
                <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontSize:15, fontWeight:800, color:"#0e0e0e", marginBottom:3 }}>{fc.title}</div>
                <div style={{ fontSize:11, color:"#555", fontWeight:300 }}>{fc.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div style={{ background:"#7dd4c0", padding:"15px 0", overflow:"hidden", whiteSpace:"nowrap" }}>
        <div style={{ display:"inline-flex", animation:"marquee 22s linear infinite" }}>
          {["UX Research","Design Systems","Interaction Design","Prototyping","Usability Testing","Information Architecture","User Flows","Branding","Super Apps","Mobile Design","UX Research","Design Systems","Interaction Design","Prototyping","Usability Testing","Information Architecture","User Flows","Branding","Super Apps","Mobile Design"].map((item,i)=>(
            <span key={i} style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:13, letterSpacing:1.2, textTransform:"uppercase", color:"#0e0e0e", padding:"0 28px", display:"inline-flex", alignItems:"center", gap:14 }}>
              {item}<span style={{ fontSize:9, opacity:.5 }}>✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── ABOUT ── */}
      <section id="about" className="lo-generic-sec" style={{ background:"#0e0e0e", padding:"120px 60px" }}>
        <div className="lo-about-inner" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:80, alignItems:"center", maxWidth:1200, margin:"0 auto" }}>
          <div className="lo-reveal">
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"rgba(255,255,255,.4)", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ display:"inline-block", width:24, height:1.5, background:"currentColor" }} /> About Me
            </div>
            <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(34px,4.5vw,58px)", letterSpacing:-2, lineHeight:.95, color:"#f8f7f4", marginBottom:24 }}>
              Complexity<br />is my <em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#7dd4c0" }}>playground.</em>
            </h2>
            <p style={{ fontSize:16, lineHeight:1.85, color:"rgba(255,255,255,.58)", fontWeight:300 }}>
              I started as an <strong style={{ color:"#f8f7f4", fontWeight:600 }}>industrial and product designer</strong> — thinking in 3D, designing physical objects, writing manufacturer specs. That background wired my brain differently. When I moved into UI/UX, I brought <strong style={{ color:"#f8f7f4", fontWeight:600 }}>systems-thinking, rigorous handoff discipline, and a love of hard problems</strong> with me.
              <br /><br />
              Now I design digital products across wildly different domains — sports, HR, pet care, fitness, supply chain — because <strong style={{ color:"#f8f7f4", fontWeight:600 }}>good design thinking isn't industry-specific.</strong>
            </p>
          </div>
          <div className="lo-stagger" style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {[
              { icon:"🔍", bg:"rgba(125,212,192,.15)", name:"User Research & Testing",       desc:"Interviews, usability tests, competitive analysis" },
              { icon:"🏗️", bg:"rgba(184,168,212,.15)", name:"Design Systems & Branding",      desc:"Built from scratch for multi-module products" },
              { icon:"⚡", bg:"rgba(168,200,232,.15)", name:"Interaction & Interface Design", desc:"Complex flows made simple and delightful" },
              { icon:"🗺️", bg:"rgba(125,212,192,.15)", name:"Information Architecture",       desc:"Multi-persona navigation and content structure" },
              { icon:"🤝", bg:"rgba(184,168,212,.15)", name:"Cross-functional Collaboration", desc:"Product, engineering, business alignment" },
            ].map((s,i)=>(
              <div key={i} className="lo-skill-row">
                <div style={{ width:44, height:44, borderRadius:12, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{s.icon}</div>
                <div>
                  <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:700, fontSize:15, color:"#f8f7f4" }}>{s.name}</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,.38)", marginTop:2 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROJECTS ── */}
      <ProjectsSection onOpenCaseStudy={openCaseStudy} />

      {/* ── PROCESS ── */}
      <section id="process" className="lo-generic-sec" style={{ background:"#1a1624", padding:"120px 60px", color:"white" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div className="lo-reveal">
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"rgba(255,255,255,.3)", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ display:"inline-block", width:24, height:1.5, background:"currentColor" }} /> How I Work
            </div>
            <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(34px,4.5vw,58px)", letterSpacing:-2, lineHeight:.95, color:"white" }}>
              My design<br /><em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#7dd4c0" }}>process.</em>
            </h2>
          </div>
          <div className="lo-process-grid lo-stagger" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:22, marginTop:56 }}>
            {[
              { num:"01", icon:"🔍", title:"Discover", desc:"User interviews, competitive analysis, and stakeholder alignment. I understand the real problem before touching a frame." },
              { num:"02", icon:"🗺️", title:"Define",   desc:"Personas, user journeys, information architecture. Every flow is mapped before it's designed." },
              { num:"03", icon:"✏️", title:"Design",   desc:"Wireframes to high-fidelity. Iterating fast, testing assumptions, building a cohesive design system in parallel." },
              { num:"04", icon:"🧪", title:"Validate", desc:"Usability testing with real users across all persona types. I don't ship on assumptions — I ship on evidence." },
            ].map((s,i)=>(
              <div key={i} className="lo-step">
                <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontSize:46, fontWeight:900, color:"rgba(255,255,255,.06)", lineHeight:1, marginBottom:18, letterSpacing:-2 }}>{s.num}</div>
                <span style={{ fontSize:26, marginBottom:12, display:"block" }}>{s.icon}</span>
                <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:800, fontSize:18, color:"white", marginBottom:9 }}>{s.title}</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,.42)", lineHeight:1.72, fontWeight:300 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="lo-generic-sec" style={{ background:"linear-gradient(135deg,#e8f8f4,#f0ecf8)", padding:"120px 60px" }}>
        <div className="lo-reveal" style={{ maxWidth:860, margin:"0 auto", textAlign:"center" }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"#999", marginBottom:14, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            <span style={{ display:"inline-block", width:24, height:1.5, background:"currentColor" }} /> Let's Work Together
          </div>
          <h2 style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:"clamp(38px,5.5vw,70px)", letterSpacing:-2, lineHeight:.95, color:"#0e0e0e", marginBottom:20 }}>
            Got a complex<br /><em style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", fontWeight:400, color:"#7dd4c0", display:"block" }}>problem?</em>
          </h2>
          <p style={{ fontSize:16, color:"#555", lineHeight:1.75, fontWeight:300, maxWidth:480, margin:"0 auto 20px" }}>
            I'm currently open to new opportunities — full-time, freelance, or remote. Drop me a message below or reach out directly.
          </p>
          <div className="lo-contact-row" style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", marginBottom:16 }}>
            <a href="mailto:abouhussein.lobna@gmail.com" className="lo-btn-primary" style={{ textDecoration:"none" }}>✉ Send an Email</a>
            <a href="https://www.linkedin.com/in/lobnaabouhussein/" target="_blank" rel="noreferrer" className="lo-btn-secondary" style={{ textDecoration:"none" }}>LinkedIn</a>
            {/* ── RESUME BUTTON 3 already counted above — this is Contact section, same button ── */}
            <a href={RESUME_LINK} target="_blank" rel="noreferrer" onClick={handleResumeClick} className="lo-btn-secondary" style={{ textDecoration:"none" }}>Download Resume ↗︎</a>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16, margin:"36px 0", maxWidth:560, marginLeft:"auto", marginRight:"auto" }}>
            <div style={{ flex:1, height:1, background:"rgba(0,0,0,.1)" }} />
            <span style={{ fontSize:12, color:"#aaa", fontWeight:500, letterSpacing:.5 }}>or send a message</span>
            <div style={{ flex:1, height:1, background:"rgba(0,0,0,.1)" }} />
          </div>
          <ContactForm />
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lo-footer" style={{ background:"#0e0e0e", padding:"38px 60px", display:"flex", justifyContent:"space-between", alignItems:"center", color:"rgba(255,255,255,.35)", fontSize:13 }}>
        <div style={{ fontFamily:"'Cabinet Grotesk',sans-serif", fontWeight:900, fontSize:16, color:"white", letterSpacing:-.3 }}>
          Lobna<span style={{ color:"#7dd4c0" }}>.</span>
        </div>
        <div>UX/UI Designer · Cairo, Egypt · 2026</div>
        <div>Built with intention ✦</div>
      </footer>

      <Analytics debug={true} />
    </>
  );
}
